// src/lib/supabase.js - Versão corrigida
import { createClient } from '@supabase/supabase-js';

// Configuração do Supabase
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

let supabase = null;
let authHelpers = null;
let dataHelpers = null;

// Rate limiting local
const rateLimiter = {
  lastSignUpAttempt: 0,
  signUpCooldown: 30000, // 30 segundos
  
  canAttemptSignUp() {
    const now = Date.now();
    const timeSinceLastAttempt = now - this.lastSignUpAttempt;
    return timeSinceLastAttempt >= this.signUpCooldown;
  },
  
  recordSignUpAttempt() {
    this.lastSignUpAttempt = Date.now();
  },
  
  getRemainingCooldown() {
    const now = Date.now();
    const timeSinceLastAttempt = now - this.lastSignUpAttempt;
    const remaining = this.signUpCooldown - timeSinceLastAttempt;
    return Math.max(0, Math.ceil(remaining / 1000));
  }
};

if (supabaseUrl && supabaseAnonKey) {
  try {
    supabase = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true
      }
    });

    // Helper functions for authentication
    authHelpers = {
      // Sign up new user and company
      async signUp(userData) {
        try {
          // Verificar rate limiting local
          if (!rateLimiter.canAttemptSignUp()) {
            const remainingSeconds = rateLimiter.getRemainingCooldown();
            throw new Error(`Muitas tentativas de cadastro. Tente novamente em ${remainingSeconds} segundos.`);
          }

          rateLimiter.recordSignUpAttempt();

          console.log('Starting signup process with data:', {
            email: userData.adminEmail,
            schoolName: userData.schoolName,
            cnpj: userData.cnpj
          });

          // 1. Criar usuário primeiro
          const { data: authData, error: authError } = await supabase.auth.signUp({
            email: userData.adminEmail,
            password: userData.password,
            options: {
              data: {
                full_name: userData.adminName
              }
            }
          });
          
          if (authError) {
            console.error('Auth error:', authError);
            
            if (authError.status === 429) {
              throw new Error('Muitas tentativas de cadastro. Aguarde alguns minutos antes de tentar novamente.');
            }
            if (authError.message?.includes('User already registered')) {
              throw new Error('Este email já está cadastrado. Tente fazer login ou use outro email.');
            }
            if (authError.message?.includes('Password should be at least')) {
              throw new Error('A senha deve ter pelo menos 6 caracteres.');
            }
            if (authError.message?.includes('Invalid email')) {
              throw new Error('Email inválido. Verifique o formato do email.');
            }
            throw new Error(authError.message || 'Erro ao criar conta');
          }

          if (!authData.user) {
            throw new Error('Erro ao criar usuário - dados não retornados');
          }

          console.log('User created successfully:', authData.user.id);

          // 2. Criar empresa - Usando apenas campos que existem na tabela
          const companyData = {
            name: userData.schoolName,
            document: userData.cnpj?.replace(/\D/g, ''), // Remove formatação
            email: userData.email,
            phone: userData.phone?.replace(/\D/g, ''), // Remove formatação
            address: userData.address || null,
            city: userData.city || null,
            active: true
            // Removidos: state, zip_code, created_at (podem não existir na tabela)
          };

          console.log('Creating company with data:', companyData);

          const { data: company, error: companyError } = await supabase
            .from('companies')
            .insert([companyData])
            .select()
            .single();

          if (companyError) {
            console.error('Company creation error:', companyError);
            console.error('Company error details:', JSON.stringify(companyError, null, 2));
            
            // Tentar deletar o usuário criado se a empresa falhar
            // Comentado pois não temos permissão de admin
            // try {
            //   await supabase.auth.admin.deleteUser(authData.user.id);
            // } catch (deleteError) {
            //   console.error('Error deleting user after company failure:', deleteError);
            // }
            
            throw new Error('Erro ao criar empresa: ' + companyError.message);
          }

          console.log('Company created successfully:', company);

          // 3. Criar pessoa/administrador - Usando apenas campos que existem
          const personData = {
            company_id: company.id,
            auth_user_id: authData.user.id,
            type: 'admin',
            name: userData.adminName,
            email: userData.adminEmail,
            phone: userData.adminPhone?.replace(/\D/g, '') || null,
            active: true
            // Removidos: role, created_at (podem não existir na tabela)
          };

          console.log('Creating person with data:', personData);

          const { data: person, error: personError } = await supabase
            .from('people')
            .insert([personData])
            .select()
            .single();

          if (personError) {
            console.error('Person creation error:', personError);
            console.error('Person error details:', JSON.stringify(personError, null, 2));
            
            // Se falhar ao criar person, deletar company apenas (não user)
            // Comentado pois não temos permissão de admin para deletar user
            try {
              await supabase.from('companies').delete().eq('id', company.id);
              // await supabase.auth.admin.deleteUser(authData.user.id);
            } catch (cleanupError) {
              console.error('Error during cleanup:', cleanupError);
            }
            
            throw new Error('Erro ao criar administrador: ' + personError.message);
          }

          console.log('Person created successfully:', person);

          return {
            user: authData.user,
            company,
            person,
            session: authData.session,
            needsConfirmation: !authData.user?.email_confirmed_at && !authData.session
          };

        } catch (error) {
          console.error('Error during sign up:', error);
          throw error;
        }
      },

      // Sign in user
      async signIn(email, password) {
        try {
          console.log('Starting signin process for:', email);

          const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
            email,
            password
          });
          
          if (authError) {
            console.error('Auth signin error:', authError);
            
            if (authError.status === 400 && authError.message?.includes('Invalid login credentials')) {
              throw new Error('Email ou senha incorretos.');
            }
            if (authError.message?.includes('Email not confirmed')) {
              throw new Error('Email não confirmado. Verifique sua caixa de entrada.');
            }
            throw new Error(authError.message || 'Erro ao fazer login');
          }

          if (!authData.user) {
            throw new Error('Dados do usuário não retornados');
          }

          console.log('User authenticated successfully:', authData.user.id);

          // Buscar dados do usuário (person + company)
          const { data: person, error: personError } = await supabase
            .from('people')
            .select(`
              *,
              companies (*)
            `)
            .eq('auth_user_id', authData.user.id)
            .eq('active', true)
            .single();

          if (personError) {
            console.error('Error loading user profile:', personError);
            
            if (personError.code === 'PGRST116') {
              throw new Error('Perfil de usuário não encontrado. Entre em contato com o suporte.');
            }
            
            // Se não conseguir carregar perfil, ainda assim permite login básico
            console.warn('Could not load user profile, proceeding with basic auth');
            return {
              user: authData.user,
              session: authData.session,
              person: null,
              company: null
            };
          }

          console.log('User profile loaded successfully:', person);

          return {
            user: authData.user,
            session: authData.session,
            person,
            company: person.companies
          };

        } catch (error) {
          console.error('Error during sign in:', error);
          throw error;
        }
      },

      // Sign out user
      async signOut() {
        try {
          const { error } = await supabase.auth.signOut();
          if (error) {
            console.error('Signout error:', error);
            throw error;
          }
          console.log('User signed out successfully');
        } catch (error) {
          console.error('Error during sign out:', error);
          throw error;
        }
      },

      // Get current session
      async getSession() {
        try {
          const { data: { session }, error } = await supabase.auth.getSession();
          if (error) {
            console.error('Session error:', error);
            throw error;
          }
          
          if (!session) {
            console.log('No active session found');
            return null;
          }
          
          console.log('Active session found for user:', session.user.id);
          
          // Get user profile data
          const { data: person, error: personError } = await supabase
            .from('people')
            .select(`
              *,
              companies (*)
            `)
            .eq('auth_user_id', session.user.id)
            .eq('active', true)
            .single();

          if (personError) {
            console.warn('Could not load user profile during session check:', personError);
            return {
              user: session.user,
              session,
              person: null,
              company: null
            };
          }

          return {
            user: session.user,
            session,
            person,
            company: person.companies
          };
        } catch (error) {
          console.error('Error getting session:', error);
          return null;
        }
      },

      // Reset password
      async resetPassword(email) {
        try {
          const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/reset-password`
          });
          if (error) {
            if (error.status === 429) {
              throw new Error('Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.');
            }
            throw new Error(error.message || 'Erro ao enviar email de recuperação');
          }
        } catch (error) {
          console.error('Error resetting password:', error);
          throw error;
        }
      }    };

    // Helper functions for data operations
    dataHelpers = {
      // Dashboard metrics
      async getDashboardMetrics(companyId) {
        try {
          console.log('📊 getDashboardMetrics - Getting dashboard metrics for company:', companyId);
            // Today's sales
          const today = new Date();
          const todayStr = today.toISOString().split('T')[0];
          const todayLocalTime = new Date(today.getTime() - (today.getTimezoneOffset() * 60000));
          const todayLocalStr = todayLocalTime.toISOString().split('T')[0];
          
          console.log('📅 getDashboardMetrics - Data de hoje (UTC):', todayStr);
          console.log('📅 getDashboardMetrics - Data de hoje (Local):', todayLocalStr);
          console.log('📅 getDashboardMetrics - Timezone offset:', today.getTimezoneOffset());
            const { data: todaySales, error: todaySalesError } = await supabase
            .from('sales')
            .select('total_amount, created_at')
            .eq('company_id', companyId)
            .gte('created_at', `${todayLocalStr}T00:00:00`)
            .lte('created_at', `${todayLocalStr}T23:59:59`);

          console.log('📊 getDashboardMetrics - Vendas de hoje encontradas:', {
            count: todaySales?.length || 0,
            data: todaySales,
            error: todaySalesError
          });

          if (todaySalesError) {
            console.error('Error getting today sales:', todaySalesError);
          }

          // Month's sales
          const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
            .toISOString().split('T')[0];
          const { data: monthSales, error: monthSalesError } = await supabase
            .from('sales')
            .select('total_amount')
            .eq('company_id', companyId)
            .gte('created_at', `${firstDayOfMonth}T00:00:00`);

          if (monthSalesError) {
            console.error('Error getting month sales:', monthSalesError);
          }

          // Active students count
          const { count: studentsCount, error: studentsError } = await supabase
            .from('students')
            .select('*', { count: 'exact', head: true })
            .eq('company_id', companyId)
            .eq('active', true);

          if (studentsError) {
            console.error('Error getting students count:', studentsError);
          }

          // Products count
          const { count: productsCount, error: productsError } = await supabase
            .from('products')
            .select('*', { count: 'exact', head: true })
            .eq('company_id', companyId)
            .eq('active', true);

          if (productsError) {
            console.error('Error getting products count:', productsError);
          }          const result = {
            todayTotal: todaySales?.reduce((sum, sale) => sum + parseFloat(sale.total_amount || 0), 0) || 0,
            todayTransactions: todaySales?.length || 0, // Número de vendas hoje
            monthTotal: monthSales?.reduce((sum, sale) => sum + parseFloat(sale.total_amount || 0), 0) || 0,
            studentsCount: studentsCount || 0,
            productsCount: productsCount || 0,
            lowStockProducts: [] // Implementar depois
          };

          console.log('📊 getDashboardMetrics - Resultado final:', result);
          return result;
        } catch (error) {
          console.error('Error getting dashboard metrics:', error);          // Retornar dados mock se falhar
          return {
            todayTotal: 0,
            todayTransactions: 0,
            monthTotal: 0,
            studentsCount: 0,
            productsCount: 0,
            lowStockProducts: []
          };
        }
      },

      // Products
      async getProducts(companyId) {
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .eq('company_id', companyId)
          .eq('active', true)
          .order('name');
        
        if (error) throw error;
        return data || [];
      },

      async createProduct(companyId, productData) {
        try {
          let imageData = null;

          // Upload da imagem se fornecida
          if (productData.image && productData.image instanceof File) {
            console.log('Uploading product image...');
            imageData = await this.uploadImage(productData.image, 'images', 'products');
          }

          // Preparar dados do produto
          const finalProductData = {
            ...productData,
            company_id: companyId,
            image_url: imageData?.url || productData.image_url || null,
            image_path: imageData?.path || productData.image_path || null
          };

          // Remover o arquivo da imagem dos dados antes de inserir
          delete finalProductData.image;

          const { data, error } = await supabase
            .from('products')
            .insert([finalProductData])
            .select()
            .single();
          
          if (error) {
            // Se houve erro e tínhamos feito upload, deletar a imagem
            if (imageData) {
              try {
                await this.deleteImage(imageData.path);
              } catch (deleteError) {
                console.error('Error cleaning up uploaded image:', deleteError);
              }
            }
            throw error;
          }

          return data;
        } catch (error) {
          console.error('Error creating product:', error);
          throw error;
        }
      },

      async updateProduct(productId, productData) {
        try {
          let imageData = null;
          let oldImagePath = null;

          // Se uma nova imagem foi fornecida
          if (productData.image && productData.image instanceof File) {
            console.log('Uploading new product image...');

            // Buscar dados atuais para obter caminho da imagem antiga
            const { data: currentData } = await supabase
              .from('products')
              .select('image_path')
              .eq('id', productId)
              .single();

            oldImagePath = currentData?.image_path;

            // Upload da nova imagem
            imageData = await this.uploadImage(productData.image, 'images', 'products');
          }

          // Preparar dados para atualização
          const updateData = { ...productData };
          
          if (imageData) {
            updateData.image_url = imageData.url;
            updateData.image_path = imageData.path;
          }

          // Remover o arquivo da imagem dos dados
          delete updateData.image;

          const { data, error } = await supabase
            .from('products')
            .update(updateData)
            .eq('id', productId)
            .select()
            .single();
          
          if (error) {
            // Se houve erro e tínhamos feito upload, deletar a nova imagem
            if (imageData) {
              try {
                await this.deleteImage(imageData.path);
              } catch (deleteError) {
                console.error('Error cleaning up uploaded image:', deleteError);
              }
            }
            throw error;
          }

          // Se tudo deu certo e temos uma nova imagem, deletar a antiga
          if (imageData && oldImagePath) {
            try {
              await this.deleteImage(oldImagePath);
            } catch (deleteError) {
              console.error('Error deleting old image:', deleteError);
              // Não falhar a operação por causa disso
            }
          }

          return data;
        } catch (error) {
          console.error('Error updating product:', error);
          throw error;
        }
      },

      async getProductById(companyId, productId) {
        try {
          const { data, error } = await supabase
            .from('products')
            .select('*')
            .eq('company_id', companyId)
            .eq('id', productId)
            .single();
          
          if (error) {
            if (error.code === 'PGRST116') {
              return null; // Product not found
            }
            throw error;
          }
          return data;
        } catch (error) {
          console.error('Error getting product by ID:', error);
          throw error;
        }
      },

      async deleteProduct(productId) {
        const { error } = await supabase
          .from('products')
          .delete()
          .eq('id', productId);
        
        if (error) throw error;
        return true;
      },      // Students
      async getStudents(companyId) {
        try {          const { data, error } = await supabase
            .from('students')
            .select(`
              *,
              person:people!fk_students_person_id(*)
            `)
            .eq('company_id', companyId)
            .order('registration_number');
          
          // Se a query básica funcionar, buscar dados do responsável financeiro separadamente
          if (data && data.length > 0) {
            // Buscar dados dos responsáveis financeiros
            const financialParentIds = [...new Set(data.filter(s => s.financial_parent_id).map(s => s.financial_parent_id))];
            
            if (financialParentIds.length > 0) {
              const { data: financialParents } = await supabase
                .from('people')
                .select('*')
                .in('id', financialParentIds);
              
              // Mapear responsáveis financeiros para cada aluno
              data.forEach(student => {
                if (student.financial_parent_id) {
                  student.financial_person = financialParents?.find(p => p.id === student.financial_parent_id);
                }
              });
            }
          }
          
          if (error) throw error;
          return data || [];
        } catch (error) {
          console.error('Error getting students:', error);
          throw error;
        }
      },      async getStudentsByParentEmail(parentEmail) {
        try {
          console.log('Getting students by parent email:', parentEmail);
          
          // Primeiro, buscar pessoas (responsáveis) com este email
          const { data: parents, error: parentsError } = await supabase
            .from('people')
            .select('id')
            .eq('email', parentEmail)
            .eq('active', true);
          
          if (parentsError) {
            console.error('Error getting parents by email:', parentsError);
            throw parentsError;
          }
          
          if (!parents || parents.length === 0) {
            console.log('No parents found with this email');
            return [];
          }
          
          const parentIds = parents.map(p => p.id);
          console.log('Found parent IDs:', parentIds);
          
          // Buscar estudantes onde o person_id corresponde aos responsáveis encontrados
          const { data, error } = await supabase
            .from('students')
            .select(`
              *,
              person:people!fk_students_person_id(*),
              financial_person:people!fk_students_financial_parent_id(*)
            `)
            .in('person_id', parentIds)
            .eq('active', true);
          
          if (error) throw error;
          return data || [];
        } catch (error) {
          console.error('Error getting students by parent email:', error);
          throw error;
        }
      },

      async createStudent(companyId, studentData) {
        try {
          console.log('Creating student with data:', studentData);

          let imageData = null;

          // Upload da foto se fornecida
          if (studentData.photo && studentData.photo instanceof File) {
            console.log('Uploading student photo...');
            imageData = await this.uploadImage(studentData.photo, 'images', 'students');
          }          // 1. Criar usuário de autenticação apenas se o aluno estiver ativo E tiver email
          let authData = null;
          
          if (studentData.active === true && studentData.email && studentData.email.trim()) {
            console.log('Creating auth user for active student with email:', studentData.email);
            
            // Se não tiver senha, gerar uma senha automática
            const studentPassword = studentData.password || `Aluno${studentData.registration_number}@2024`;
            
            console.log('Attempting to create auth user with password length:', studentPassword.length);
            
            const { data: authResult, error: authError } = await supabase.auth.signUp({
              email: studentData.email,
              password: studentPassword,
              options: {
                data: {
                  full_name: studentData.name,
                  user_type: 'student'
                }
              }
            });

            if (authError) {
              console.error('Auth error creating student user:', authError);
              console.error('Auth error details:', {
                message: authError.message,
                status: authError.status,
                statusCode: authError.status_code
              });
              
              // Cleanup da imagem se foi feito upload
              if (imageData) {
                try {
                  await this.deleteImage(imageData.path);
                } catch (deleteError) {
                  console.error('Error cleaning up uploaded image:', deleteError);
                }
              }
              
              // Melhor tratamento de erros específicos
              let errorMessage = 'Erro ao criar conta de acesso para o aluno';
              if (authError.message?.includes('already registered')) {
                errorMessage = 'Este email já está cadastrado no sistema';
              } else if (authError.message?.includes('invalid email')) {
                errorMessage = 'Email inválido';
              } else if (authError.message?.includes('password')) {
                errorMessage = 'Senha inválida (mínimo 6 caracteres)';
              } else if (authError.message?.includes('rate limit')) {
                errorMessage = 'Muitas tentativas. Aguarde alguns minutos';
              } else {
                errorMessage += ': ' + authError.message;
              }
              
              throw new Error(errorMessage);
            }

            if (!authResult || !authResult.user) {
              // Cleanup da imagem se foi feito upload
              if (imageData) {
                try {
                  await this.deleteImage(imageData.path);
                } catch (deleteError) {
                  console.error('Error cleaning up uploaded image:', deleteError);
                }
              }
              throw new Error('Erro ao criar usuário - dados não retornados pelo servidor');
            }

            authData = authResult;
            console.log('Student user created successfully:', authData.user.id);
          } else {
            if (studentData.active !== true) {
              console.log('Student marked as inactive, skipping auth user creation');
            } else if (!studentData.email || !studentData.email.trim()) {
              console.log('No email provided for student, skipping auth user creation');
            }
          }// 2. Criar registro na tabela people apenas se houver usuário de autenticação
          let person = null;
          
          if (authData && authData.user) {
            console.log('Creating person record for student with auth user');
            
            const personData = {
              company_id: companyId,
              auth_user_id: authData.user.id,
              type: 'parent', // Usando 'parent' como tipo válido do enum
              name: studentData.name,
              email: studentData.email,
              phone: studentData.phone || null,
              active: true
            };

            console.log('Creating person record:', personData);

            let { data: personResult, error: personError } = await supabase
              .from('people')
              .insert([personData])
              .select()
              .single();

            // Se falhou por causa da coluna permissions, tentar sem ela
            if (personError && (personError.message?.includes('permissions') || personError.code === '42703')) {
              console.warn('Permissions column not found, trying without it...');
              
              const personDataWithoutPermissions = {
                company_id: companyId,
                auth_user_id: authData.user.id,
                type: 'parent', // Usando 'parent' como tipo válido do enum
                name: studentData.name,
                email: studentData.email,
                phone: studentData.phone || null,
                cpf: studentData.cpf || null,
                notes: studentData.notes || null,
                active: studentData.active !== false
              };

              const result = await supabase
                .from('people')
                .insert([personDataWithoutPermissions])
                .select()
                .single();
                
              personResult = result.data;
              personError = result.error;
            }

            if (personError) {
              console.error('Person creation error:', personError);
              // Cleanup da imagem se foi feito upload
              if (imageData) {
                try {
                  await this.deleteImage(imageData.path);
                } catch (deleteError) {
                  console.error('Error cleaning up uploaded image:', deleteError);
                }
              }
              throw new Error('Erro ao criar registro de pessoa: ' + personError.message);
            }

            person = personResult;
            console.log('Person created successfully:', person);          } else {
            console.log('No auth user created, skipping person record creation');
          }          // 3. Criar registro na tabela students
          const finalStudentData = {
            company_id: companyId,
            person_id: studentData.person_id || (person ? person.id : null), // usar o responsável selecionado ou a pessoa criada
            parent_id: studentData.person_id || null, // parent_id = responsável selecionado
            financial_parent_id: studentData.financial_parent_id || studentData.person_id || null, // responsável financeiro obrigatório
            name: studentData.name,
            email: studentData.email || null,
            phone: studentData.phone || null,
            registration_number: studentData.registration_number,
            class_name: studentData.class_name,
            birth_date: studentData.birth_date || null,
            address: studentData.address || null,
            city: studentData.city || null,
            state: studentData.state || null,
            zip_code: studentData.zip_code || null,
            balance: studentData.balance || 0,
            credit_limit: studentData.credit_limit || 0,
            payment_type: studentData.payment_type || 'prepaid',
            notes: studentData.notes || null,
            photo_url: imageData?.url || null,
            photo_path: imageData?.path || null,
            active: studentData.active !== false
          };

          console.log('Creating student record:', finalStudentData);          const { data: student, error: studentError } = await supabase
            .from('students')
            .insert([finalStudentData])
            .select('*')
            .single();if (studentError) {
            console.error('Student creation error:', studentError);
            // Cleanup: deletar person criado (se houver) e imagem
            try {
              if (person) {
                await supabase.from('people').delete().eq('id', person.id);
              }
              if (imageData) {
                await this.deleteImage(imageData.path);
              }
            } catch (cleanupError) {
              console.error('Error during cleanup:', cleanupError);
            }
            throw new Error('Erro ao criar registro de aluno: ' + studentError.message);
          }          console.log('Student created successfully:', student);          return {
            ...student,
            user_created: !!authData,
            has_auth_access: !!authData
          };
        } catch (error) {
          console.error('Error creating student:', error);
          throw error;
        }
      },
        async updateStudent(studentId, studentData) {
        try {
          let imageData = null;
          let oldImagePath = null;          // Buscar dados atuais do aluno para verificar se já tem usuário auth
          const { data: currentStudent, error: fetchError } = await supabase
            .from('students')
            .select(`
              photo_path, 
              person_id, 
              email,
              person:people!fk_students_person_id(auth_user_id, email)
            `)
            .eq('id', studentId)
            .single();

          if (fetchError) {
            console.error('Error fetching current student data:', fetchError);
            throw fetchError;
          }

          // Se uma nova imagem foi fornecida
          if (studentData.photo && studentData.photo instanceof File) {
            console.log('Uploading new student photo...');
            oldImagePath = currentStudent?.photo_path;
            // Upload da nova imagem
            imageData = await this.uploadImage(studentData.photo, 'images', 'students');
          }

          // Lidar com criação/atualização do usuário auth apenas se necessário
          let authUserId = currentStudent?.person?.auth_user_id;
          const currentEmail = currentStudent?.email; // Email do estudante, não da pessoa
          const newEmail = studentData.email?.trim();
          
          // Só tentar criar usuário auth se:
          // 1. Aluno está ativo
          // 2. Tem email fornecido 
          // 3. Não tem usuário auth ainda
          // 4. O email é diferente do atual (ou não tinha email antes)
          const shouldCreateAuth = studentData.active === true && 
                                 newEmail && 
                                 !authUserId && 
                                 (newEmail !== currentEmail);
          
          if (shouldCreateAuth) {
            
            const studentPassword = studentData.password || `Aluno${studentData.registration_number}@2024`;
            
            const { data: authResult, error: authError } = await supabase.auth.signUp({
              email: newEmail,
              password: studentPassword,
              options: {
                data: {
                  full_name: studentData.name,
                  user_type: 'student'
                }
              }
            });

            if (authError) {
              console.error('Auth error creating student user:', authError);
              // Cleanup da imagem se foi feito upload
              if (imageData) {
                try {
                  await this.deleteImage(imageData.path);
                } catch (deleteError) {
                  console.error('Error cleaning up uploaded image:', deleteError);
                }
              }
              
              let errorMessage = 'Erro ao criar conta de acesso para o aluno';
              if (authError.message?.includes('already registered')) {
                errorMessage = 'Este email já está cadastrado no sistema';
              }
              throw new Error(errorMessage);
            }

            authUserId = authResult.user?.id;
            
            // Atualizar o registro de pessoa com o novo auth_user_id
            if (currentStudent.person_id && authUserId) {
              await supabase
                .from('people')
                .update({ 
                  auth_user_id: authUserId,
                  user_created_at: new Date().toISOString()
                })
                .eq('id', currentStudent.person_id);
            }
          }          // Remove fields that should not be stored in students table
          const cleanStudentData = { ...studentData };          // Mapear responsável selecionado para person_id antes de remover os campos de seleção
          if (studentData.selected_parent_id) {
            cleanStudentData.person_id = studentData.selected_parent_id;
          }
          if (studentData.selected_financial_parent_id) {
            cleanStudentData.financial_parent_id = studentData.selected_financial_parent_id;
          }
          delete cleanStudentData.password;                    // Handled in auth.users table
          delete cleanStudentData.confirm_password;            // Only for form validation
          delete cleanStudentData.photo;                       // File object, not stored directly
          delete cleanStudentData.selected_parent_id;          // Only for form selection
          delete cleanStudentData.selected_financial_parent_id; // Only for form selection
          delete cleanStudentData.create_system_access;        // Only for form logic
          
          // Remove campos relacionais que vêm das queries
          delete cleanStudentData.person;
          delete cleanStudentData.financial_person;
          delete cleanStudentData.people;
          
          // Se há nova imagem, atualizar os campos relacionados
          if (imageData) {
            cleanStudentData.photo_url = imageData.url;
            cleanStudentData.photo_path = imageData.path;
          }          const { error } = await supabase
            .from('students')
            .update(cleanStudentData)
            .eq('id', studentId);
          
          if (error) {
            // Se houve erro e tínhamos feito upload, deletar a nova imagem
            if (imageData) {
              try {
                await this.deleteImage(imageData.path);
              } catch (deleteError) {
                console.error('Error cleaning up uploaded image:', deleteError);
              }
            }
            throw error;
          }
          
          // Buscar dados atualizados completos
          const { data, error: selectError } = await supabase
            .from('students')
            .select(`
              *,
              person:people!fk_students_person_id(*)
            `)
            .eq('id', studentId)
            .single();
          
          if (selectError) {
            throw selectError;
          }
          
          // Buscar dados do responsável financeiro se existir
          if (data && data.financial_parent_id) {
            const { data: financialParent } = await supabase
              .from('people')
              .select('*')
              .eq('id', data.financial_parent_id)
              .single();
            
            if (financialParent) {
              data.financial_person = financialParent;
            }
          }

          // Se tudo deu certo e temos uma nova imagem, deletar a antiga
          if (imageData && oldImagePath) {
            try {
              await this.deleteImage(oldImagePath);
            } catch (deleteError) {
              console.error('Error deleting old image:', deleteError);
              // Não falhar a operação por causa disso
            }
          }
          
          return data;
        } catch (error) {
          console.error('Error updating student:', error);
          throw error;
        }
      },

      async deleteStudent(studentId) {
        try {          // Primeiro, buscar o student para obter o person_id
          const { data: student, error: fetchError } = await supabase
            .from('students')
            .select('person_id, people!person_id(auth_user_id)')
            .eq('id', studentId)
            .single();

          if (fetchError) {
            console.error('Error fetching student for deletion:', fetchError);
            throw fetchError;
          }

          // Deletar student
          const { error: studentError } = await supabase
            .from('students')
            .delete()
            .eq('id', studentId);

          if (studentError) {
            console.error('Error deleting student:', studentError);
            throw studentError;
          }

          // Deletar person associado
          if (student.person_id) {
            const { error: personError } = await supabase
              .from('people')
              .delete()
              .eq('id', student.person_id);

            if (personError) {
              console.error('Error deleting person:', personError);
              // Não falhar se não conseguir deletar person
            }
          }

          // Note: Em produção, você pode querer desativar o usuário ao invés de deletar
          // para manter histórico de vendas, etc.

          return true;
        } catch (error) {
          console.error('Error deleting student:', error);
          throw error;
        }
      },

      async getStudentById(companyId, studentId) {
        try {          const { data, error } = await supabase
            .from('students')
            .select(`
              *,
              person:people!fk_students_person_id(*)
            `)
            .eq('company_id', companyId)
            .eq('id', studentId)
            .single();
          
          // Se encontrou o aluno, buscar dados do responsável financeiro
          if (data && data.financial_parent_id) {
            const { data: financialParent } = await supabase
              .from('people')
              .select('*')
              .eq('id', data.financial_parent_id)
              .single();
            
            data.financial_person = financialParent;
          }
          
          if (error) {
            if (error.code === 'PGRST116') {
              return null; // Student not found
            }
            throw error;
          }
          return data;
        } catch (error) {
          console.error('Error getting student by ID:', error);
          throw error;
        }
      },      // Users
      async getUsers(companyId) {
        try {
          const { data, error } = await supabase
            .from('people')
            .select('*')
            .eq('company_id', companyId)
            .in('type', ['admin', 'collaborator', 'parent'])
            .order('name');
          
          if (error) throw error;
            // Parse permissões de JSON string para objeto
          const usersWithParsedPermissions = (data || []).map(user => ({
            ...user,
            permissions: user.permissions ? 
              (typeof user.permissions === 'string' ? JSON.parse(user.permissions) : user.permissions)
              : null
          }));
          
          return usersWithParsedPermissions;
        } catch (error) {
          console.error('Error getting users:', error);
          throw error;
        }
      },

      async createUser(companyId, userData) {
        try {
          console.log('Creating user with data:', userData);

          // 1. Criar usuário de autenticação
          const { data: authData, error: authError } = await supabase.auth.signUp({
            email: userData.email,
            password: userData.password,
            options: {
              data: {
                full_name: userData.name,
                user_type: userData.type
              }
            }
          });

          if (authError) {
            console.error('Auth error creating user:', authError);
            throw new Error('Erro ao criar conta de acesso: ' + authError.message);
          }

          if (!authData.user) {
            throw new Error('Erro ao criar usuário - dados não retornados');
          }

          console.log('User created successfully:', authData.user.id);          // 2. Criar registro na tabela people
          const personData = {
            company_id: companyId,
            auth_user_id: authData.user.id,
            type: userData.type,
            name: userData.name,
            email: userData.email,
            phone: userData.phone || null,
            cpf: userData.cpf || null,
            permissions: userData.permissions ? JSON.stringify(userData.permissions) : null,
            notes: userData.notes || null,
            active: userData.active !== false
          };

          console.log('Creating person record:', personData);

          const { data: person, error: personError } = await supabase
            .from('people')
            .insert([personData])
            .select()
            .single();          if (personError) {
            console.error('Person creation error:', personError);
            console.error('Person error details:', JSON.stringify(personError, null, 2));
            
            // Verificar se é erro relacionado a permissões
            if (personError.message?.includes('permissions') || personError.code === '42703') {
              throw new Error('Erro ao definir permissões do usuário: coluna permissions não encontrada na tabela');
            }
            
            // Outros erros específicos
            if (personError.code === '23505') {
              throw new Error('Já existe um usuário com este email');
            }
            
            if (personError.code === '23503') {
              throw new Error('Erro de autorização. Verifique se você tem permissão para cadastrar usuários');
            }
            
            throw new Error('Erro ao criar registro de usuário: ' + personError.message);
          }          console.log('User created successfully:', person);

          return {
            ...person,
            permissions: person.permissions ? 
              (typeof person.permissions === 'string' ? JSON.parse(person.permissions) : person.permissions) 
              : userData.permissions || null,
            auth_user_id: authData.user.id,            user_created: true
          };
        } catch (error) {
          console.error('Error creating user:', error);
          throw error;
        }
      },

      async updateUser(userId, userData) {
        try {
          // Converter permissões para JSON string se necessário
          const updateData = {
            ...userData,
            permissions: userData.permissions ? JSON.stringify(userData.permissions) : null
          };
          
          let { data, error } = await supabase
            .from('people')
            .update(updateData)
            .eq('id', userId)
            .select()
            .single();
          
          // Se falhou por causa da coluna permissions, tentar sem ela
          if (error && (error.message?.includes('permissions') || error.code === '42703')) {
            console.warn('Permissions column not found in update, trying without it...');
            
            const updateDataWithoutPermissions = {
              ...userData,
              permissions: undefined
            };
            delete updateDataWithoutPermissions.permissions;

            const result = await supabase
              .from('people')
              .update(updateDataWithoutPermissions)
              .eq('id', userId)
              .select()
              .single();
              
            data = result.data;
            error = result.error;
          }
          
          if (error) throw error;
          
          // Parse permissões de volta para objeto
          return {
            ...data,
            permissions: data.permissions ? 
              (typeof data.permissions === 'string' ? JSON.parse(data.permissions) : data.permissions)
              : userData.permissions || null
          };
        } catch (error) {
          console.error('Error updating user:', error);
          throw error;
        }
      },      async deleteUser(userId) {
        try {
          const { error } = await supabase
            .from('people')
            .delete()
            .eq('id', userId);

          if (error) throw error;
          return true;
        } catch (error) {
          console.error('Error deleting user:', error);
          throw error;
        }
      },

      async getUser(userId) {
        try {
          const { data, error } = await supabase
            .from('people')
            .select('*')
            .eq('id', userId)
            .single();
          
          if (error) throw error;
          
          // Parse permissões de JSON string para objeto
          return {
            ...data,
            permissions: data.permissions ? 
              (typeof data.permissions === 'string' ? JSON.parse(data.permissions) : data.permissions)
              : null
          };
        } catch (error) {
          console.error('Error getting user:', error);
          throw error;
        }
      },

      // Cash Register functions
      async getCurrentCashRegister(companyId) {
        try {
          const { data, error } = await supabase
            .from('cash_registers')
            .select('*')
            .eq('company_id', companyId)
            .eq('status', 'open')
            .single();
          
          if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
            throw error;
          }
          
          return data;
        } catch (error) {
          console.error('Error getting current cash register:', error);
          throw error;
        }
      },

      async createCashRegister(registerData) {
        try {
          const { data, error } = await supabase
            .from('cash_registers')
            .insert([registerData])
            .select()
            .single();
          
          if (error) {
            console.error('Cash register creation error:', error);
            throw error;
          }
          
          return data;
        } catch (error) {
          console.error('Error creating cash register:', error);
          throw error;
        }
      },

      async updateCashRegister(registerId, updateData) {
        try {
          const { data, error } = await supabase
            .from('cash_registers')
            .update(updateData)
            .eq('id', registerId)
            .select()
            .single();
          
          if (error) {
            console.error('Cash register update error:', error);
            throw error;
          }
          
          return data;
        } catch (error) {
          console.error('Error updating cash register:', error);
          throw error;
        }
      },

      async getCashMovements(registerId) {
        try {
          const { data, error } = await supabase
            .from('cash_movements')
            .select('*')
            .eq('cash_register_id', registerId)
            .order('created_at', { ascending: false });
          
          if (error) throw error;
          return data || [];
        } catch (error) {
          console.error('Error getting cash movements:', error);
          throw error;
        }
      },

      async createCashMovement(movementData) {
        try {
          const { data, error } = await supabase
            .from('cash_movements')
            .insert([movementData])
            .select()
            .single();
          
          if (error) {
            console.error('Cash movement creation error:', error);
            throw error;
          }
          
          return data;
        } catch (error) {
          console.error('Error creating cash movement:', error);
          throw error;
        }
      },

      async calculateSystemBalance(registerId) {
        try {
          // Buscar dados do registro de caixa
          const { data: register, error: registerError } = await supabase
            .from('cash_registers')
            .select('opening_balance, opened_at, closed_at')
            .eq('id', registerId)
            .single();

          if (registerError) throw registerError;

          // Calcular vendas em dinheiro no período
          let salesQuery = supabase
            .from('sales')
            .select('total_amount')
            .eq('payment_method', 'cash')
            .gte('created_at', register.opened_at);

          if (register.closed_at) {
            salesQuery = salesQuery.lte('created_at', register.closed_at);
          }

          const { data: sales, error: salesError } = await salesQuery;
          if (salesError) throw salesError;

          const cashSales = sales?.reduce((sum, sale) => sum + parseFloat(sale.total_amount || 0), 0) || 0;

          // Calcular movimentações de caixa
          const { data: movements, error: movementsError } = await supabase
            .from('cash_movements')
            .select('type, amount')
            .eq('cash_register_id', registerId);

          if (movementsError) throw movementsError;

          const movementsTotal = movements?.reduce((sum, movement) => {
            const amount = parseFloat(movement.amount || 0);
            return movement.type === 'entry' ? sum + amount : sum - amount;
          }, 0) || 0;

          const systemBalance = parseFloat(register.opening_balance) + cashSales + movementsTotal;
          return systemBalance;
        } catch (error) {
          console.error('Error calculating system balance:', error);
          throw error;
        }
      },

      async getCashRegistersHistory(companyId, options = {}) {
        try {
          let query = supabase
            .from('cash_registers')
            .select(`
              *,
              opened_by_name:opened_by(name),
              closed_by_name:closed_by(name)
            `)
            .eq('company_id', companyId);

          if (options.startDate) {
            query = query.gte('opened_at', options.startDate);
          }
          if (options.endDate) {
            query = query.lte('opened_at', options.endDate);
          }

          query = query.order('opened_at', { ascending: false });

          const { data, error } = await query;
          if (error) {
            console.error('Error getting cash registers history:', error);
            throw error;
          }
          
          // Format the data to flatten the joined names
          const formattedData = (data || []).map(register => ({
            ...register,
            opened_by_name: register.opened_by_name?.name || null,
            closed_by_name: register.closed_by_name?.name || null
          }));
          
          return formattedData;
        } catch (error) {
          console.error('Error in getCashRegistersHistory function:', error);
          throw error;
        }
      },

      // Banks
      async getBanks(companyId) {
        try {
          const { data, error } = await supabase
            .from('banks')
            .select('*')
            .eq('company_id', companyId)
            .order('name');
          
          if (error) throw error;
          return data || [];
        } catch (error) {
          console.error('Error getting banks:', error);
          throw error;
        }
      },

      async createBank(companyId, bankData) {
        try {
          console.log('Creating bank with data:', bankData);

          // Preparar dados do banco
          const finalBankData = {
            ...bankData,
            company_id: companyId
          };

          const { data, error } = await supabase
            .from('banks')
            .insert([finalBankData])
            .select()
            .single();
          
          if (error) {
            console.error('Bank creation error:', error);
            throw error;
          }

          console.log('Bank created successfully:', data);
          return data;
        } catch (error) {
          console.error('Error creating bank:', error);
          throw error;
        }
      },

      async updateBank(bankId, bankData) {
        try {
          console.log('Updating bank with data:', bankData);

          const { data, error } = await supabase
            .from('banks')
            .update(bankData)
            .eq('id', bankId)
            .select()
            .single();
          
          if (error) {
            console.error('Bank update error:', error);
            throw error;
          }

          console.log('Bank updated successfully:', data);
          return data;
        } catch (error) {
          console.error('Error updating bank:', error);
          throw error;
        }
      },

      async getBankById(companyId, bankId) {
        try {
          const { data, error } = await supabase
            .from('banks')
            .select('*')
            .eq('company_id', companyId)
            .eq('id', bankId)
            .single();
          
          if (error) {
            if (error.code === 'PGRST116') {
              return null; // Bank not found
            }
            throw error;
          }
          return data;
        } catch (error) {
          console.error('Error getting bank by ID:', error);
          throw error;
        }
      },

      async deleteBank(bankId) {
        try {
          const { error } = await supabase
            .from('banks')
            .delete()
            .eq('id', bankId);

          if (error) throw error;
          return true;
        } catch (error) {
          console.error('Error deleting bank:', error);
          throw error;
        }
      },

      async testBankApiConnection(bankId, apiConfig) {
        try {
          console.log('Testing bank API connection:', apiConfig);
          
          // Aqui você implementaria a lógica específica para testar cada provedor
          // Por exemplo, para Banco do Brasil, Itaú, etc.
          
          // Por enquanto, simular o teste
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Simular sucesso/falha baseado na configuração
          if (!apiConfig.api_url || !apiConfig.client_id) {
            throw new Error('Configurações incompletas');
          }
          
          return {
            success: true,
            message: 'Conexão testada com sucesso!',
            provider: apiConfig.provider,
            environment: apiConfig.environment
          };
        } catch (error) {
          console.error('Error testing bank API connection:', error);
          return {
            success: false,
            message: `Erro ao testar conexão: ${error.message}`,
            error: error.message
          };
        }
      },

      // Sales
      async getSales(companyId, options = {}) {
        try {
          let query = supabase
            .from('sales')
            .select(`
              *,
              students:student_id (
                id,
                name,
                registration_number,
                class_name
              )
            `)
            .eq('company_id', companyId);

          if (options.startDate) {
            query = query.gte('created_at', options.startDate);
          }
          if (options.endDate) {
            query = query.lte('created_at', options.endDate);
          }
          if (options.limit) {
            query = query.limit(options.limit);
          }

          query = query.order('created_at', { ascending: false });

          const { data, error } = await query;
          if (error) {
            console.error('Error getting sales:', error);
            throw error;
          }
          return data || [];
        } catch (error) {
          console.error('Error in getSales function:', error);
          throw error;
        }
      },

      async getSaleById(saleId, companyId) {
        try {
          const { data, error } = await supabase
            .from('sales')
            .select(`
              *,
              students:student_id (
                id,
                name,
                registration_number,
                class_name
              )
            `)
            .eq('id', saleId)
            .eq('company_id', companyId)
            .single();
          
          if (error) {
            if (error.code === 'PGRST116') {
              return null; // Sale not found
            }
            throw error;
          }
          return data;
        } catch (error) {
          console.error('Error getting sale by ID:', error);
          throw error;
        }
      },

      async createSale(companyId, saleData) {
        try {
          console.log('Creating sale for company:', companyId);
          console.log('Sale data:', saleData);
          
          const { data, error } = await supabase
            .from('sales')
            .insert([{ ...saleData, company_id: companyId }])
            .select()
            .single();
          
          if (error) {
            console.error('Supabase sale creation error:', error);
            throw error;
          }
            console.log('Sale created successfully:', data);
          
          // Criar transação financeira para TODAS as vendas
          try {
            // Buscar saldo atual do aluno se há student_id e pagamento for com saldo
            let balanceBefore = 0;
            let balanceAfter = 0;
            
            if (data.student_id && data.payment_method === 'student_balance') {
              const student = await this.getStudentById(companyId, data.student_id);
              balanceBefore = parseFloat(student?.balance || 0);
              balanceAfter = balanceBefore - parseFloat(data.total_amount || 0);
              
              // Atualizar saldo do aluno
              await this.updateStudent(data.student_id, { balance: balanceAfter });
            }
              // Criar transação financeira (sempre, independente se tem aluno ou não)
            const itemsDescription = Array.isArray(data.items) 
              ? data.items.map(item => item.name || item.product_name || 'Produto').join(', ')
              : typeof data.items === 'string' 
                ? data.items 
                : 'Produtos diversos';
            
            const transactionData = {
              company_id: companyId,
              student_id: data.student_id || null, // null se não houver aluno selecionado
              sale_id: data.id,
              type: 'sale',
              description: `Venda PDV - ${itemsDescription}`,
              amount: parseFloat(data.total_amount) || 0, // Valor positivo
              transaction_type: 'debit', // Débito para vendas
              balance_before: balanceBefore,
              balance_after: balanceAfter,
              payment_method: data.payment_method,
              payment_reference: data.payment_reference,
              details: {
                items: itemsDescription,
                operator: data.operator_name,
                payment_method: data.payment_method,
                sale_id: data.id,
                total_amount: parseFloat(data.total_amount || 0),
                student_name: data.student_name || null
              },
              status: 'completed',
              created_by: saleData.created_by || data.seller_id
            };
            
            await this.createFinancialTransaction(companyId, transactionData);
            console.log('Financial transaction created for sale:', data.id);
          } catch (transactionError) {
            console.error('Error creating financial transaction for sale:', transactionError);
            // Não falhar a venda por causa disso, apenas logar o erro
          }
          
          return data;
        } catch (error) {
          console.error('Error in createSale function:', error);
          throw error;
        }
      },

      // Função para criar transação financeira
      async createFinancialTransaction(companyId, transactionData) {
        try {
          console.log('🔥 Criando transação financeira:', transactionData);
          
          // Garantir que os campos obrigatórios estejam presentes
          const requiredTransaction = {
            id: crypto.randomUUID(),
            company_id: companyId,
            type: transactionData.type || 'sale',
            description: transactionData.description || 'Transação',
            amount: parseFloat(transactionData.amount) || 0,
            created_by: transactionData.created_by,
            created_at: new Date().toISOString(),
            status: transactionData.status || 'completed',
            ...transactionData
          };

          console.log('📝 Dados da transação preparados:', requiredTransaction);

          const { data, error } = await supabase
            .from('financial_transactions')
            .insert([requiredTransaction])
            .select()
            .single();

          if (error) {
            console.error('❌ Erro ao criar transação financeira:', error);
            throw error;
          }

          console.log('✅ Transação financeira criada com sucesso:', data);
          return data;

        } catch (error) {
          console.error('❌ Erro na função createFinancialTransaction:', error);
          throw error;
        }
      },

      // Função para processar recarga de crédito do estudante
      async processStudentRecharge(companyId, studentId, amount, paymentMethod, paymentReference, createdBy, description = null) {
        try {
          console.log('💰 Processando recarga de estudante:', {
            studentId,
            amount,
            paymentMethod,
            paymentReference
          });

          // Buscar saldo atual do estudante
          const student = await this.getStudentById(companyId, studentId);
          if (!student) {
            throw new Error('Estudante não encontrado');
          }

          const balanceBefore = parseFloat(student.balance) || 0;
          const rechargeAmount = parseFloat(amount) || 0;
          const balanceAfter = balanceBefore + rechargeAmount;

          // Atualizar saldo do estudante
          await this.updateStudent(studentId, { balance: balanceAfter });

          // Criar transação financeira para a recarga
          const transactionData = {
            company_id: companyId,
            student_id: studentId,
            type: 'recharge',
            description: description || `Recarga de crédito - ${student.name}`,
            amount: rechargeAmount,
            transaction_type: 'credit', // Crédito para recargas
            balance_before: balanceBefore,
            balance_after: balanceAfter,
            payment_method: paymentMethod,
            payment_reference: paymentReference,
            details: {
              student_name: student.name,
              student_id: studentId,
              payment_method: paymentMethod,
              recharge_amount: rechargeAmount
            },
            status: 'completed',
            created_by: createdBy
          };

          const transaction = await this.createFinancialTransaction(companyId, transactionData);
          
          console.log('✅ Recarga processada com sucesso:', {
            studentName: student.name,
            balanceBefore,
            balanceAfter,
            transactionId: transaction.id
          });

          return {
            student: { ...student, balance: balanceAfter },
            transaction,
            balanceBefore,
            balanceAfter
          };

        } catch (error) {
          console.error('❌ Erro ao processar recarga:', error);
          throw error;
        }
      },

      // Image upload functions
      async uploadImage(file, bucket = 'images', folder = '') {
        try {
          // Gerar nome único para o arquivo
          const fileExt = file.name.split('.').pop();
          const fileName = `${folder ? folder + '/' : ''}${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

          console.log('Uploading image:', fileName);

          const { data, error } = await supabase.storage
            .from(bucket)
            .upload(fileName, file, {
              cacheControl: '3600',
              upsert: false
            });

          if (error) {
            console.error('Storage upload error:', error);
            throw error;
          }

          // Obter URL público da imagem
          const { data: { publicUrl } } = supabase.storage
            .from(bucket)
            .getPublicUrl(fileName);

          return {
            path: fileName,
            url: publicUrl,
            size: file.size,
            type: file.type
          };
        } catch (error) {
          console.error('Error uploading image:', error);
          throw error;
        }
      },

      async deleteImage(imagePath, bucket = 'images') {
        try {
          const { error } = await supabase.storage
            .from(bucket)
            .remove([imagePath]);

          if (error) {
            console.error('Storage delete error:', error);
            throw error;
          }

          return true;
        } catch (error) {
          console.error('Error deleting image:', error);
          throw error;
        }
      },      async getImageUrl(imagePath, bucket = 'images') {
        if (!imagePath) return null;
        
        try {
          const { data: { publicUrl } } = supabase.storage
            .from(bucket)
            .getPublicUrl(imagePath);

          return publicUrl;
        } catch (error) {
          console.error('Error getting image URL:', error);
          return null;
        }
      },

      // Funções para configurações da empresa
      async getCompanySettings(companyId) {
        try {
          const { data, error } = await supabase
            .from('company_settings')
            .select('*')
            .eq('company_id', companyId)
            .single();

          if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
            throw error;
          }

          // Se não existir configuração, retorna configurações padrão
          if (!data) {
            return {
              general: {
                companyName: '',
                cnpj: '',
                email: '',
                phone: '',
                address: '',
                city: '',
                state: '',
                zipCode: '',
                timezone: 'America/Sao_Paulo',
                currency: 'BRL',
                taxRate: 0,
                language: 'pt-BR'
              },
              whatsapp: {
                enabled: false,
                apiUrl: '',
                apiKey: '',
                instanceName: '',
                webhookUrl: '',
                autoResponse: true,
                welcomeMessage: 'Olá! Bem-vindo à nossa cantina. Como posso ajudá-lo?',
                balanceMessage: 'Seu saldo atual é: R$ {balance}',
                lowBalanceAlert: true,
                lowBalanceThreshold: 10.00,
                paymentConfirmation: true,
                receiptMessage: 'Recibo da sua compra:\n{receipt}',
                businessHours: {
                  enabled: true,
                  start: '07:00',
                  end: '17:00',
                  message: 'Nosso atendimento funciona das 7h às 17h. Retornaremos em breve!'
                }
              },
              notifications: {
                emailNotifications: true,
                whatsappNotifications: false,
                lowStockAlert: true,
                lowBalanceAlert: true
              }
            };
          }

          return {
            general: data.general_settings || {},
            whatsapp: data.whatsapp_settings || {},
            notifications: data.notification_settings || {}
          };
        } catch (error) {
          console.error('Erro ao carregar configurações:', error);
          throw error;
        }
      },

      async updateCompanySettings(companyId, settings) {
        try {
          const { data, error } = await supabase
            .from('company_settings')
            .upsert(
              {
                company_id: companyId,
                general_settings: settings.general,
                whatsapp_settings: settings.whatsapp,
                notification_settings: settings.notifications,
                updated_at: new Date().toISOString()
              },
              { onConflict: ['company_id'] }
            )
            .select();

          if (error) throw error;
          return data[0];
        } catch (error) {
          console.error('Erro ao salvar configurações:', error);
          throw error;
        }
      },

      // ============================================================================
      // CONFIGURAÇÕES PIX
      // ============================================================================
      
      async getPixSettings(companyId) {
        try {
          const { data, error } = await supabase
            .from('pix_settings')
            .select('*')
            .eq('company_id', companyId)
            .single();
          
          if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
            console.error('Erro ao buscar configurações PIX:', error);
            throw error;
          }
            // Se não encontrar configurações, retornar configuração padrão
          if (!data) {
            return {
              id: null,
              company_id: companyId,
              token: '',
              webhook_url: '',
              api_url: 'https://sandbox.asaas.com',
              environment: 'sandbox',
              active: false,
              default_customer_id: null,
              created_at: null,
              updated_at: null
            };
          }
          
          return data;
        } catch (error) {
          console.error('Erro ao buscar configurações PIX:', error);
          throw error;
        }
      },

      async savePixSettings(companyId, settings) {
        try {
          // Verificar se já existe configuração para a empresa
          const { data: existing } = await supabase
            .from('pix_settings')
            .select('id')
            .eq('company_id', companyId)
            .single();
            const pixData = {
            company_id: companyId,
            token: settings.token || '',
            webhook_url: settings.webhook_url || '',
            webhook_email: settings.webhook_email || '',
            webhook_auth_token: settings.webhook_auth_token || '',
            api_url: settings.api_url || 'https://sandbox.asaas.com',
            environment: settings.environment || 'sandbox',
            active: settings.active || false,
            default_customer_id: settings.default_customer_id || null,
            updated_at: new Date().toISOString()
          };
          
          if (existing) {
            // Atualizar configuração existente
            const { data, error } = await supabase
              .from('pix_settings')
              .update(pixData)
              .eq('company_id', companyId)
              .select()
              .single();
            
            if (error) {
              console.error('Erro ao atualizar configurações PIX:', error);
              throw error;
            }
            
            return data;
          } else {
            // Criar nova configuração
            pixData.created_at = new Date().toISOString();
            
            const { data, error } = await supabase
              .from('pix_settings')
              .insert(pixData)
              .select()
              .single();
            
            if (error) {
              console.error('Erro ao criar configurações PIX:', error);
              throw error;
            }
            
            return data;
          }
        } catch (error) {
          console.error('Erro ao salvar configurações PIX:', error);
          throw error;
        }
      },

      async testAsaasConnection(companyId) {
        try {
          const { data, error } = await supabase.functions.invoke('asaas-test-connection', {
            body: {
              company_id: companyId
            }
          });

          if (error) throw error;
          return data;
        } catch (error) {
          console.error('Erro ao testar conexão Asaas:', error);
          return {
            success: false,
            message: `Erro de conexão: ${error.message}`
          };
        }
      },

      async configureAsaasWebhook(companyId, webhookUrl, email) {
        try {
          const { data, error } = await supabase.functions.invoke('asaas-configure-webhook', {
            body: {
              company_id: companyId,
              url: webhookUrl,
              email: email,
              enabled: true
            }
          });

          if (error) throw error;

          if (data.success) {
            // Buscar configurações atuais e atualizar com o authToken
            const currentSettings = await this.getPixSettings(companyId);
            await this.savePixSettings(companyId, {
              ...currentSettings,
              webhook_auth_token: data.authToken,
              webhook_email: email,
              webhook_url: webhookUrl
            });
          }

          return data;
        } catch (error) {
          console.error('Erro ao configurar webhook Asaas:', error);
          throw error;
        }
      },

      async getAsaasWebhookStatus(companyId) {
        try {
          const { data, error } = await supabase.functions.invoke('asaas-get-webhook-status', {
            body: { company_id: companyId }
          });

          if (error) throw error;
          return data;
        } catch (error) {
          console.error('Erro ao verificar status do webhook:', error);
          throw error;
        }
      },

      async getWebhookLogs(limit = 50) {
        try {
          const { data, error } = await supabase
            .from('webhook_logs')
            .select('*')
            .order('received_at', { ascending: false })
            .limit(limit);

          if (error) throw error;
          return data;
        } catch (error) {
          console.error('Erro ao buscar logs do webhook:', error);
          throw error;
        }
      },

      async testWebhookCallback(studentId, amount, eventType = 'PAYMENT_RECEIVED') {
        try {
          const { data, error } = await supabase.functions.invoke('test-webhook-callback', {
            body: {
              student_id: studentId,
              amount: amount,
              event_type: eventType
            }
          });

          if (error) throw error;
          return data;
        } catch (error) {
          console.error('Erro ao testar webhook:', error);
          throw error;
        }
      },

      // CLIENTE CONSUMIDOR FINAL
      // ============================================================================
      
      async createDefaultCustomer(companyId) {
        try {
          console.log('🏗️ Criando cliente "Consumidor Final" para empresa:', companyId);
          
          // Buscar dados da empresa
          const { data: company, error: companyError } = await supabase
            .from('companies')
            .select('name, document, email')
            .eq('id', companyId)
            .single();
            
          if (companyError || !company) {
            throw new Error('Erro ao buscar dados da empresa');
          }
          
          // Criar cliente no Asaas usando os dados da empresa
          const customerData = {
            company_id: companyId,
            customer: {
              name: 'Consumidor Final',
              cpfCnpj: company.document,
              email: company.email,
              mobilePhone: '', // Vazio por ser consumidor final
              address: '',     // Pode ser preenchido posteriormente se necessário
              externalReference: `consumidor-final-${companyId}`
            }
          };
          
          const { data, error } = await supabase.functions.invoke('asaas-create-customer', {
            body: customerData
          });

          if (error) {
            console.error('❌ Erro ao criar cliente no Asaas:', error);
            throw error;
          }
          
          if (!data || !data.id) {
            throw new Error('Cliente criado mas ID não retornado');
          }
          
          console.log('✅ Cliente "Consumidor Final" criado no Asaas:', data.id);
          
          // Salvar o ID do cliente nas configurações PIX
          const currentSettings = await this.getPixSettings(companyId);
          await this.savePixSettings(companyId, {
            ...currentSettings,
            default_customer_id: data.id
          });
          
          console.log('✅ ID do cliente salvo nas configurações PIX');
          
          return {
            success: true,
            customer_id: data.id,
            customer_data: data,
            message: 'Cliente "Consumidor Final" criado com sucesso!'
          };
          
        } catch (error) {
          console.error('❌ Erro ao criar cliente "Consumidor Final":', error);
          return {
            success: false,
            message: `Erro ao criar cliente: ${error.message}`
          };
        }
      },

      async getDefaultCustomer(companyId) {
        try {
          const settings = await this.getPixSettings(companyId);
          return settings.default_customer_id || null;
        } catch (error) {
          console.error('Erro ao buscar ID do cliente padrão:', error);
          return null;
        }
      },

      // Funções de mensalidades
      async getMonthlySubscribers(companyId) {
        try {
          const { data, error } = await supabase
            .from('monthly_subscribers')
            .select(`
              *,
              students:student_id (
                id,
                name,
                registration_number,
                class_name
              ),
              monthly_plans:monthly_plan_id (
                id,
                name,
                monthly_value
              )
            `)
            .eq('company_id', companyId)
            .order('created_at', { ascending: false });

          if (error) throw error;
          return data || [];
        } catch (error) {
          console.error('Error getting monthly subscribers:', error);
          throw error;
        }
      },

      async getMonthlyPlans(companyId) {
        try {
          const { data, error } = await supabase
            .from('monthly_plans')
            .select('*')
            .eq('company_id', companyId)
            .eq('active', true)
            .order('monthly_value', { ascending: true });

          if (error) throw error;
          return data || [];
        } catch (error) {
          console.error('Error getting monthly plans:', error);
          throw error;
        }
      },

      async getMonthlyInvoices(companyId) {
        try {
          const { data, error } = await supabase
            .from('monthly_invoices')
            .select(`
              *,
              monthly_subscribers!monthly_invoices_monthly_subscriber_id_fkey (
                id,
                contract_code,
                students:student_id (
                  id,
                  name,
                  registration_number
                ),
                monthly_plans:monthly_plan_id (
                  id,
                  name,
                  monthly_value
                )
              )
            `)
            .eq('company_id', companyId)
            .order('due_date', { ascending: false });

          if (error) throw error;
          return data || [];
        } catch (error) {
          console.error('Error getting monthly invoices:', error);
          throw error;
        }
      },

      async createMonthlySubscriber(companyId, subscriberData) {
        try {
          // Validate contract code uniqueness before insertion
          if (subscriberData.contract_code) {
            const uniquenessCheck = await this.checkContractCodeUniqueness(companyId, subscriberData.contract_code);
            if (!uniquenessCheck.isUnique) {
              throw new Error(`Código de contrato '${subscriberData.contract_code}' já existe. Por favor, gere um novo código.`);
            }
          }

          const { data, error } = await supabase
            .from('monthly_subscribers')
            .insert([{ ...subscriberData, company_id: companyId }])
            .select(`
              *,
              students:student_id (
                id,
                name,
                registration_number,
                class_name
              ),
              monthly_plans:monthly_plan_id (
                id,
                name,
                monthly_value
              )
            `)
            .single();
          
          if (error) {
            // Check if it's a contract code duplicate constraint violation
            if (error.code === '23505' && error.message.includes('idx_monthly_subscribers_contract_code')) {
              throw new Error('Código de contrato já existe. Por favor, gere um novo código e tente novamente.');
            }
            throw error;
          }
          return data;
        } catch (error) {
          console.error('Error creating monthly subscriber:', error);
          throw error;
        }
      },

      async checkContractCodeUniqueness(companyId, contractCode) {
        try {
          const { data, error } = await supabase
            .from('monthly_subscribers')
            .select('id')
            .eq('company_id', companyId)
            .eq('contract_code', contractCode)
            .limit(1);

          if (error) throw error;
          
          return {
            isUnique: !data || data.length === 0
          };
        } catch (error) {
          console.error('Error checking contract code uniqueness:', error);
          return { isUnique: false };
        }
      },      async getPeople(companyId) {
        try {
          const { data, error } = await supabase
            .from('people')
            .select('*')
            .eq('company_id', companyId)
            .eq('active', true)
            .order('name', { ascending: true });

          if (error) throw error;
          return data || [];
        } catch (error) {
          console.error('Error getting people:', error);
          throw error;
        }      },

      // Implementação real do getTodayStats para o dashboard
      async getTodayStats(companyId) {
        try {
          console.log('📊 getTodayStats - Buscando estatísticas do dia para company:', companyId);
          
          // Definir período: hoje (início do dia até agora)
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const startOfDay = today.toISOString();
          
          console.log('📅 Data filtro - Início do dia:', startOfDay);
          console.log('📅 Data atual completa:', new Date().toISOString());
          
          // Definir ontem (para comparação)
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          yesterday.setHours(0, 0, 0, 0);
          const startOfYesterday = yesterday.toISOString();
          const endOfYesterday = today.toISOString();
          
          // Buscar vendas do dia
          console.log('🔍 Buscando vendas desde:', startOfDay);
          const { data: sales, error: salesError } = await supabase
            .from('sales')
            .select('id, total_amount, payment_method, created_at')
            .eq('company_id', companyId)
            .gte('created_at', startOfDay);
              if (salesError) {
            console.error('Erro ao buscar vendas do dia:', salesError);
            throw salesError;
          }
          
          console.log('📊 Vendas encontradas hoje:', sales?.length || 0);
          console.log('📊 Dados das vendas de hoje:', sales);
          
          // Buscar vendas de ontem para comparação
          const { data: yesterdaySales, error: yesterdayError } = await supabase
            .from('sales')
            .select('id, total_amount, created_at')
            .eq('company_id', companyId)
            .gte('created_at', startOfYesterday)
            .lt('created_at', endOfYesterday);
          
          if (yesterdayError) {
            console.error('Erro ao buscar vendas de ontem:', yesterdayError);
            // Não lançar erro, apenas continuar com dados parciais
          }
          
          // Calcular total e contagem
          const todayTotal = sales?.reduce((sum, sale) => sum + parseFloat(sale.total_amount || 0), 0) || 0;
          const salesCount = sales?.length || 0;
          
          // Calcular total de ontem
          const yesterdayTotal = yesterdaySales?.reduce((sum, sale) => sum + parseFloat(sale.total_amount || 0), 0) || 0;
          
          // Calcular variação percentual
          let changePercentage = 0;
          if (yesterdayTotal > 0) {
            changePercentage = Math.round(((todayTotal - yesterdayTotal) / yesterdayTotal) * 100);
          }
          
          // Calcular ticket médio
          const averageTicket = salesCount > 0 ? todayTotal / salesCount : 0;
          
          // Contagem por método de pagamento
          const paymentMethodCounts = sales?.reduce((acc, sale) => {
            const method = sale.payment_method || 'outros';
            acc[method] = (acc[method] || 0) + 1;
            return acc;
          }, {}) || {};
            // Calcular distribuição por hora
          const hourlyData = sales?.reduce((acc, sale) => {
            const hour = new Date(sale.created_at).getHours();
            acc[hour] = acc[hour] || { count: 0, total: 0 };
            acc[hour].count += 1;
            acc[hour].total += parseFloat(sale.total_amount || 0);
            return acc;
          }, {}) || {};          // Transformar hourlyData em formato esperado pelo dashboard
          const salesByHour = Object.entries(hourlyData).map(([hour, data]) => ({
            time: `${String(hour).padStart(2, '0')}:00`,
            amount: data.total,
            transactions: data.count
          })).sort((a, b) => a.time.localeCompare(b.time));
          
          return {
            todayTotal,
            salesCount,
            averageTicket,
            paymentMethodCounts,
            hourlyData,
            sales: salesByHour, // Usar dados agregados por hora
            // Adicionando a comparação por hora esperada pelo dashboard
            hourlyComparison: {
              current: todayTotal,
              yesterday: yesterdayTotal,
              change: changePercentage
            }
          };        } catch (error) {
          console.error('Error in getTodayStats:', error);
          // Retornar valores padrão em caso de erro, incluindo a estrutura hourlyComparison
          return {
            todayTotal: 0,
            salesCount: 0,
            averageTicket: 0,
            paymentMethodCounts: {},
            hourlyData: {},
            sales: [], // Array vazio no formato correto
            hourlyComparison: {
              current: 0,
              yesterday: 0,
              change: 0
            }
          };
        }
      },      // Função para buscar vendas recentes
      async getRecentSales(companyId, limit = 5) {
        try {
          console.log('🔍 getRecentSales - Iniciando busca para company:', companyId);
          
          const { data, error } = await supabase
            .from('sales')
            .select(`
              id,
              total_amount,
              payment_method,
              created_at,
              items,
              students:student_id (
                id,
                name,
                registration_number,
                class_name
              )
            `)
            .eq('company_id', companyId)            
            .order('created_at', { ascending: false })
            .limit(limit);

          console.log('🔍 getRecentSales - Query result:', { data, error, hasData: !!data, dataLength: data?.length });

          if (error) {
            console.error('🔍 getRecentSales - Supabase error:', error);
            throw error;          }
          
          // Transformar os dados para o formato esperado pelo dashboard
          const formattedSales = (data || []).map((sale, index) => {
            console.log(`🔍 getRecentSales - Processando venda ${index}:`, {
              id: sale.id,
              total_amount: sale.total_amount,
              payment_method: sale.payment_method,
              created_at: sale.created_at,
              items: sale.items,
              students: sale.students
            });
            
            // Formatar items como string
            let itemsText = '';
            if (sale.items && Array.isArray(sale.items)) {
              itemsText = sale.items.map(item => 
                `${item.product_name || item.name || 'Produto'} (${item.quantity || 1}x)`
              ).join(', ');
            } else if (sale.items) {
              itemsText = 'Itens da venda';
            } else {
              itemsText = 'Venda';
            }

            // Formatar horário - garantir que seja uma data válida
            let timeText = '';
            try {
              const saleTime = new Date(sale.created_at);
              console.log(`🔍 getRecentSales - Data original: ${sale.created_at}, Data parseada:`, saleTime);
              if (!isNaN(saleTime.getTime())) {
                timeText = saleTime.toLocaleTimeString('pt-BR', { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                });
                console.log(`🔍 getRecentSales - Horário formatado: ${timeText}`);
              } else {
                console.log('🔍 getRecentSales - Data inválida, usando --:--');
                timeText = '--:--';
              }
            } catch (error) {
              console.error('🔍 getRecentSales - Erro ao formatar data:', error);
              timeText = '--:--';
            }

            // Formatar método de pagamento
            const paymentMap = {
              'cash': 'Dinheiro',
              'credit': 'Cartão',
              'pix': 'PIX',
              'balance': 'Saldo'
            };

            // Garantir que amount seja um número válido
            const rawAmount = sale.total_amount;
            const amount = parseFloat(rawAmount) || 0;
            console.log(`🔍 getRecentSales - Valor bruto: ${rawAmount}, Valor parseado: ${amount}`);

            const formattedSale = {
              id: sale.id,
              student: sale.students ? sale.students.name : 'Consumidor Final',
              class: sale.students ? sale.students.class_name : '',
              items: itemsText,
              time: timeText,
              payment: paymentMap[sale.payment_method] || sale.payment_method || 'Outros',
              amount: amount
            };
            
            console.log(`🔍 getRecentSales - Venda formatada ${index}:`, formattedSale);
            return formattedSale;          });

          console.log('🔍 getRecentSales - Resultado final:', formattedSales);
          return formattedSales;
        } catch (error) {
          console.error('🔍 getRecentSales - Error:', error);
          return [];
        }
      },

      // Função para buscar produtos com estoque baixo
      async getLowStockProducts(companyId, limit = 5) {
        try {
          const { data, error } = await supabase
            .from('products')
            .select('*')
            .eq('company_id', companyId)
            .lt('stock_quantity', 10) // Produtos com menos de 10 unidades
            .gt('stock_quantity', 0)  // Mas ainda com algum estoque
            .order('stock_quantity', { ascending: true })
            .limit(limit);          if (error) throw error;
          
          // Transformar os dados para o formato esperado pelo dashboard
          const formattedProducts = (data || []).map(product => {
            const stockLevel = product.stock_quantity || 0;
            const alertThreshold = product.alert_threshold || 10;
            
            return {
              id: product.id,
              name: product.name,
              current: stockLevel,
              status: stockLevel <= 3 ? 'critical' : 'low',
              price: product.price || product.cost_price || 0
            };
          });

          return formattedProducts;
        } catch (error) {
          console.error('Error getting low stock products:', error);
          return [];
        }      },      // Métricas adicionais do dashboard
      async getAdditionalMetrics(companyId) {
        try {
          // Buscar vendas de hoje para calcular ticket médio
          const today = new Date().toISOString().split('T')[0];
          const { data: todaySales, error } = await supabase
            .from('sales')
            .select('total_amount')
            .eq('company_id', companyId)
            .gte('created_at', `${today}T00:00:00`)
            .lte('created_at', `${today}T23:59:59`);

          if (error) {
            console.error('Error getting additional metrics:', error);
            return {
              todayTransactions: 0,
              averageTicket: 0
            };
          }

          const salesCount = todaySales?.length || 0;
          const totalAmount = todaySales?.reduce((sum, sale) => sum + parseFloat(sale.total_amount || 0), 0) || 0;
          const averageTicket = salesCount > 0 ? totalAmount / salesCount : 0;

          return {
            todayTransactions: salesCount,
            averageTicket: averageTicket
          };
        } catch (error) {
          console.error('Error in getAdditionalMetrics:', error);
          return {
            todayTransactions: 0,
            averageTicket: 0
          };
        }
      },// Funções de estoque/inventário
      async getInventoryMovements(companyId) {
        try {
          const { data, error } = await supabase
            .from('inventory_movements')
            .select(`
              *,
              products:product_id (
                id,
                name,
                category
              ),
              creator:created_by (
                id,
                name
              )
            `)
            .eq('company_id', companyId)
            .order('created_at', { ascending: false });

          if (error) {
            // Se a tabela não existir, retornar array vazio
            if (error.code === 'PGRST200' || error.message?.includes('inventory_movements')) {
              console.warn('Inventory movements table not found, returning empty array');
              return [];
            }
            throw error;
          }
          return data || [];
        } catch (error) {
          console.error('Error getting inventory movements:', error);
          return [];
        }
      },      async createInventoryMovement(companyId, movementData) {
        try {
          const { data, error } = await supabase
            .from('inventory_movements')
            .insert([{
              ...movementData,
              company_id: companyId,
              created_at: new Date().toISOString()
            }])
            .select(`
              *,
              products:product_id (
                id,
                name,
                category
              ),
              creator:created_by (
                id,
                name
              )
            `)
            .single();

          if (error) {
            // Se a tabela não existir, retornar dados mock
            if (error.code === 'PGRST200' || error.message?.includes('inventory_movements')) {
              console.warn('Inventory movements table not found, returning mock data');
              return {
                id: `temp-movement-${Date.now()}`,
                ...movementData,
                company_id: companyId,
                created_at: new Date().toISOString(),
                products: {
                  id: movementData.product_id,
                  name: 'Produto',
                  category: 'Geral'
                },
                creator: {
                  id: movementData.created_by,
                  name: 'Usuário'
                }
              };
            }
            throw error;
          }

          // Atualizar o estoque do produto se o movimento foi criado com sucesso
          if (data && data.product_id) {
            try {
              // Buscar o produto atual
              const { data: product, error: productError } = await supabase
                .from('products')
                .select('stock_quantity')
                .eq('id', data.product_id)
                .single();

              if (!productError && product) {
                // Calcular novo estoque
                const currentStock = product.stock_quantity || 0;
                const quantity = data.quantity || 0;
                const newStock = data.type === 'in' 
                  ? currentStock + quantity 
                  : currentStock - quantity;

                // Atualizar estoque do produto
                const { error: updateError } = await supabase
                  .from('products')
                  .update({ stock_quantity: newStock })
                  .eq('id', data.product_id);

                if (updateError) {
                  console.error('Error updating product stock:', updateError);
                }
              }
            } catch (stockUpdateError) {
              console.error('Error updating stock after movement:', stockUpdateError);
              // Não falhar a operação por causa do erro de atualização de estoque
            }
          }

          return data;
        } catch (error) {
          console.error('Error creating inventory movement:', error);
          throw error;
        }
      },

      // FUNÇÕES PARA PORTAL DOS PAIS
      // ============================================================================

      async getMessagesByRecipient(recipientId, options = {}) {
        try {
          console.log('Getting messages by recipient:', recipientId);
          
          const { data, error } = await supabase
            .from('messages')
            .select(`
              *,
              message_recipients!inner(
                id,
                read_at,
                delivered_at,
                recipient_id,
                student_id
              )
            `)
            .eq('message_recipients.recipient_id', recipientId)
            .eq('is_active', true)
            .order('created_at', { ascending: false });
          
          if (error) throw error;
          
          // Transformar os dados para facilitar o uso no frontend
          const formattedData = (data || []).map(message => ({
            ...message,
            message_id: message.id,
            read_at: message.message_recipients[0]?.read_at,
            delivered_at: message.message_recipients[0]?.delivered_at,
            recipient_info: message.message_recipients[0]
          }));
          
          return formattedData;
        } catch (error) {
          console.error('Error getting messages by recipient:', error);
          throw error;
        }
      },

      async markMessageAsRead(messageId, recipientId) {
        try {
          console.log('Marking message as read:', { messageId, recipientId });
          
          const { data, error } = await supabase
            .from('message_recipients')
            .update({
              read_at: new Date().toISOString()
            })
            .eq('message_id', messageId)
            .eq('recipient_id', recipientId)
            .select();
          
          if (error) throw error;
          
          console.log('Message marked as read successfully:', data);
          return data;
        } catch (error) {
          console.error('Error marking message as read:', error);
          throw error;
        }
      },

      async getParentFinancialSummary(parentId, options = {}) {
        try {
          console.log('Getting parent financial summary:', parentId);
          
          let actualParentId = parentId;
          
          let companyId;
          
          // Se parentId parece ser um email, buscar estudantes por email do responsável
          if (typeof parentId === 'string' && parentId.includes('@')) {
            console.log('Parent ID is email, searching students by parent email...');
            
            // Buscar estudantes relacionados ao email do responsável
            const students = await this.getStudentsByParentEmail(parentId);
            
            if (!students || students.length === 0) {
              console.warn('No students found for parent email:', parentId);
              return {
                totalBalance: 0,
                totalSpent: 0,
                totalRecharges: 0,
                studentsCount: 0,
                students: []
              };
            }
            
            // Usar o primeiro estudante para pegar company_id e financial_parent_id
            const firstStudent = students[0];
            companyId = firstStudent.company_id;
            actualParentId = firstStudent.financial_parent_id;
            
            console.log('Found parent ID from students:', actualParentId, 'company:', companyId);
            
            // Retornar dados baseados nos estudantes já encontrados
            let totalBalance = 0;
            let totalSpent = 0;
            let totalRecharges = 0;
            
            // Buscar saldos de todos os estudantes
            for (const student of students) {
              const balance = await this.getStudentBalance(student.id);
              totalBalance += balance || 0;
            }
            
            // Buscar transações financeiras de todos os estudantes de uma vez
            const studentIds = students.map(s => s.id);
            const { data: allTransactions, error: transError } = await supabase
              .from('financial_transactions')
              .select('type, amount, student_id')
              .in('student_id', studentIds);
            
            if (!transError && allTransactions) {
              for (const transaction of allTransactions) {
                if (transaction.type === 'recharge') {
                  totalRecharges += transaction.amount || 0;
                } else if (transaction.type === 'purchase') {
                  totalSpent += transaction.amount || 0;
                }
              }
            }
            
            return {
              totalBalance,
              totalSpent,
              totalRecharges,
              studentsCount: students.length,
              students: students.map(s => ({ id: s.id, name: s.name, registration_number: s.registration_number }))
            };
          }
          
          // Caso seja UUID, buscar estudantes normalmente (código original)
          // Buscar todos os estudantes do responsável
          const { data: students, error: studentsError } = await supabase
            .from('students')
            .select('id, name, registration_number, company_id')
            .eq('financial_parent_id', actualParentId)
            .eq('active', true);
          
          if (studentsError) throw studentsError;
          
          if (!students || students.length === 0) {
            return {
              totalBalance: 0,
              totalSpent: 0,
              totalRecharges: 0,
              studentsCount: 0,
              students: []
            };
          }
          
          // Calcular resumo financeiro de todos os estudantes (código UUID)
          let totalBalance = 0;
          let totalSpent = 0;
          let totalRecharges = 0;
          
          // Buscar saldos de todos os estudantes
          for (const student of students) {
            const balance = await this.getStudentBalance(student.id);
            totalBalance += balance || 0;
          }
          
          // Buscar transações financeiras de todos os estudantes de uma vez
          const studentIds = students.map(s => s.id);
          const { data: allTransactions, error: transError } = await supabase
            .from('financial_transactions')
            .select('type, amount, student_id')
            .in('student_id', studentIds);
          
          if (!transError && allTransactions) {
            for (const transaction of allTransactions) {
              if (transaction.type === 'recharge') {
                totalRecharges += transaction.amount || 0;
              } else if (transaction.type === 'purchase') {
                totalSpent += transaction.amount || 0;
              }
            }
          }
          
          return {
            totalBalance,
            totalSpent,
            totalRecharges,
            studentsCount: students.length,
            students: students.map(s => ({ id: s.id, name: s.name, registration_number: s.registration_number }))
          };
        } catch (error) {
          console.error('Error getting parent financial summary:', error);
          throw error;
        }
      },

      // Função compatível com chamadas que passam companyId como primeiro parâmetro
      async getStudentFinancialExtract(param1, param2, param3) {
        // Se param3 existe, então param1 é companyId, param2 é studentId, param3 é options
        // Se param3 não existe, então param1 é studentId, param2 é options
        let studentId, options;
        
        if (param3 !== undefined) {
          // Chamada com 3 parâmetros: (companyId, studentId, options)
          studentId = param2;
          options = param3 || {};
          console.log('Getting student financial extract (with companyId):', { companyId: param1, studentId, options });
        } else {
          // Chamada com 2 parâmetros: (studentId, options)
          studentId = param1;
          options = param2 || {};
          console.log('Getting student financial extract:', { studentId, options });
        }
        
        try {
          const { limit = 50, offset = 0, startDate, endDate } = options;
          
          console.log('🔍 Searching financial_transactions for student:', studentId);
          
          // Primeiro vamos verificar se existem transações para este estudante
          const { data: allTransactions, error: countError } = await supabase
            .from('financial_transactions')
            .select('*')
            .eq('student_id', studentId);
          
          console.log('📊 All transactions found:', allTransactions?.length || 0, allTransactions);
          
          if (countError) {
            console.error('Error counting transactions:', countError);
            throw countError;
          }
          
          // Se não encontrou nenhuma, tentar com studant_id (possível typo na tabela)
          if (!allTransactions || allTransactions.length === 0) {
            console.log('🔍 Trying with studant_id field...');
            const { data: altTransactions, error: altError } = await supabase
              .from('financial_transactions')
              .select('*')
              .eq('studant_id', studentId);
            
            console.log('📊 Transactions with studant_id:', altTransactions?.length || 0, altTransactions);
            
            if (altError) {
              console.error('Error with studant_id:', altError);
            }
            
            if (altTransactions && altTransactions.length > 0) {
              return altTransactions;
            }
          }
          
          // Buscar transações financeiras do estudante com filtros e paginação
          let query = supabase
            .from('financial_transactions')
            .select('*, transaction_type::text')
            .eq('student_id', studentId)
            .order('created_at', { ascending: false });
          
          // Aplicar filtros de data se fornecidos
          if (startDate) {
            query = query.gte('created_at', startDate);
          }
          if (endDate) {
            query = query.lte('created_at', endDate);
          }
          
          // Aplicar paginação
          query = query.range(offset, offset + limit - 1);
          
          const { data: transactions, error } = await query;
          
          if (error) throw error;
          
          console.log('📋 Transactions before enrichment:', transactions?.length || 0);
          
          if (!transactions || transactions.length === 0) {
            return [];
          }
          
          // Enriquecer transações com dados de vendas e produtos
          const enrichedTransactions = await Promise.all(
            transactions.map(async (transaction) => {
              console.log('🔍 Processing transaction:', transaction.id, 'sale_id:', transaction.sale_id);
              let saleDetails = null;
              let items = [];
              
              // Se a transação tem sale_id, buscar detalhes da venda
              if (transaction.sale_id) {
                try {
                  const { data: sale, error: saleError } = await supabase
                    .from('sales')
                    .select('*')
                    .eq('id', transaction.sale_id)
                    .single();
                  
                  if (!saleError && sale) {
                    console.log('✅ Sale found:', sale.id, 'items:', sale.items);
                    saleDetails = sale;
                    
                    // Se a venda tem items, buscar detalhes dos produtos
                    if (sale.items && Array.isArray(sale.items)) {
                      const productIds = sale.items.map(item => item.product_id).filter(Boolean);
                      
                      if (productIds.length > 0) {
                        const { data: products, error: productsError } = await supabase
                          .from('products')
                          .select('id, name, description, price, category')
                          .in('id', productIds);
                        
                        if (!productsError && products) {
                          // Combinar dados do item da venda com dados do produto
                          items = sale.items.map(item => {
                            const product = products.find(p => p.id === item.product_id);
                            return {
                              ...item,
                              product_name: product?.name || 'Produto não encontrado',
                              product_description: product?.description || '',
                              product_category: product?.category || '',
                              product_current_price: product?.price || 0
                            };
                          });
                        }
                      }
                    }
                  }
                } catch (saleError) {
                  console.warn('Error fetching sale details for transaction:', transaction.id, saleError);
                }
              } else {
                console.log('❌ No sale_id for transaction:', transaction.id);
              }
              
              return {
                ...transaction,
                sale_details: saleDetails,
                items: items
              };
            })
          );
          
          console.log('📋 Final enriched transactions returned:', enrichedTransactions?.length || 0);
          
          return enrichedTransactions;
        } catch (error) {
          console.error('Error getting student financial extract:', error);
          throw error;
        }
      },

      async getParentAllStudentsTransactions(parentEmail, options = {}) {
        try {
          console.log('🌍 Getting all transactions for parent:', parentEmail);
          
          const { limit = 100, offset = 0, startDate, endDate } = options;
          
          // 1. Buscar todos os estudantes vinculados ao responsável
          const students = await this.getStudentsByParentEmail(parentEmail);
          
          if (!students || students.length === 0) {
            console.log('❌ No students found for parent:', parentEmail);
            return [];
          }
          
          console.log('📚 Found students:', students.map(s => s.name));
          
          // 2. Buscar transações financeiras de todos os estudantes
          const studentIds = students.map(s => s.id);
          
          let query = supabase
            .from('financial_transactions')
            .select('*')
            .in('student_id', studentIds)
            .order('created_at', { ascending: false });
          
          // Aplicar filtros de data se fornecidos
          if (startDate) {
            query = query.gte('created_at', startDate);
          }
          if (endDate) {
            query = query.lte('created_at', endDate);
          }
          
          // Aplicar paginação
          query = query.range(offset, offset + limit - 1);
          
          const { data: transactions, error } = await query;
          
          if (error) throw error;
          
          console.log('💰 Found transactions:', transactions?.length || 0);
          
          if (!transactions || transactions.length === 0) {
            return [];
          }
          
          // 3. Enriquecer transações com dados dos estudantes e produtos
          const enrichedTransactions = await Promise.all(
            transactions.map(async (transaction) => {
              // Encontrar o estudante correspondente
              const student = students.find(s => s.id === transaction.student_id);
              
              let saleDetails = null;
              let items = [];
              
              // Se a transação tem sale_id, buscar detalhes da venda
              if (transaction.sale_id) {
                try {
                  const { data: sale, error: saleError } = await supabase
                    .from('sales')
                    .select('*')
                    .eq('id', transaction.sale_id)
                    .single();
                  
                  if (!saleError && sale) {
                    saleDetails = sale;
                    
                    // Se a venda tem items, buscar detalhes dos produtos
                    if (sale.items && Array.isArray(sale.items)) {
                      const productIds = sale.items.map(item => item.product_id).filter(Boolean);
                      
                      if (productIds.length > 0) {
                        const { data: products, error: productsError } = await supabase
                          .from('products')
                          .select('id, name, description, price, category')
                          .in('id', productIds);
                        
                        if (!productsError && products) {
                          items = sale.items.map(item => {
                            const product = products.find(p => p.id === item.product_id);
                            return {
                              ...item,
                              product_name: product?.name || 'Produto não encontrado',
                              product_description: product?.description || '',
                              product_category: product?.category || '',
                              product_current_price: product?.price || 0
                            };
                          });
                        }
                      }
                    }
                  }
                } catch (saleError) {
                  console.warn('Error fetching sale details for transaction:', transaction.id, saleError);
                }
              }
              
              return {
                ...transaction,
                student_name: student?.name || 'Estudante não encontrado',
                student_registration: student?.registration_number || '',
                student_class: student?.class_name || '',
                sale_details: saleDetails,
                items: items
              };
            })
          );
            console.log('✅ All transactions enriched:', enrichedTransactions.length);
          
          return enrichedTransactions;
        } catch (error) {
          console.error('Error getting parent all students transactions:', error);
          throw error;
        }
      },

      // Função para buscar saldo atualizado de um estudante específico
      async getStudentBalance(studentId) {
        try {
          console.log('💰 Buscando saldo real do estudante:', studentId);
          
          const { data: student, error } = await supabase
            .from('students')
            .select('balance')
            .eq('id', studentId)
            .single();
          
          if (error) {
            console.error('Erro ao buscar saldo do estudante:', error);
            throw error;
          }
          
          if (!student) {
            console.warn('Estudante não encontrado:', studentId);
            return 0;
          }
          
          const balance = parseFloat(student.balance) || 0;
          console.log(`💰 Saldo atual do estudante ${studentId}:`, balance);
          return balance;
        } catch (error) {
          console.error('Erro ao buscar saldo:', error);
          throw error;
        }
      }
    };

    console.log('Supabase configured successfully');

  } catch (error) {
    console.error('Error configuring Supabase:', error);
    supabase = null;
    authHelpers = null;
    dataHelpers = null;
  }
} else {
  console.warn('Supabase environment variables not found');
}

// Fallback to mock data if Supabase not configured
if (!supabase || !authHelpers || !dataHelpers) {
  console.warn('🎭 Using mock data - Supabase not properly configured');
  console.log('🔍 Debug info:', { 
    supabase: !!supabase, 
    authHelpers: !!authHelpers, 
    dataHelpers: !!dataHelpers 
  });
  
  authHelpers = {
    async signUp(userData) {
      console.log('Mock sign up:', userData);
      await new Promise(resolve => setTimeout(resolve, 2000));
      return {
        user: { id: 'mock-user', email: userData.adminEmail },
        company: { id: 'mock-company', name: userData.schoolName },
        person: { id: 'mock-person', name: userData.adminName, type: 'admin' },
        session: { access_token: 'mock-token' }
      };
    },
    
    async signIn(email, password) {
      console.log('Mock sign in:', email);
      await new Promise(resolve => setTimeout(resolve, 1500));
      return {
        user: { id: 'mock-user', email },
        session: { access_token: 'mock-token' },
        person: { id: 'mock-person', name: 'Demo User', type: 'admin' },
        company: { id: 'mock-company', name: 'Escola Demo' }
      };
    },
    
    async signOut() {
      console.log('Mock sign out');
      return Promise.resolve();
    },
    
    async getSession() {
      return null;
    },
    
    async resetPassword(email) {
      console.log('Mock password reset:', email);
      return Promise.resolve();
    },

      // ============================================================================
      // SISTEMA DE AVALIAÇÕES/REVIEWS
      // ============================================================================
      
      async getReviews(companyId, filters = {}) {
        try {
          let query = supabase
            .from('reviews')
            .select(`

              *,
              parent:parent_id (
                id,
                name,
                email,
                phone
              ),
              student:student_id (
                id,
                name,
                registration_number,
                class_name
              )
            `)
            .eq('company_id', companyId)
            .order('created_at', { ascending: false });

          // Aplicar filtros opcionais
          if (filters.rating) {
            query = query.eq('rating', filters.rating);
          }

          if (filters.student_id) {
            query = query.eq('student_id', filters.student_id);
          }

          if (filters.parent_id) {
            query = query.eq('parent_id', filters.parent_id);
          }

          if (filters.startDate) {
            query = query.gte('created_at', filters.startDate);
          }

          if (filters.endDate) {
            query = query.lte('created_at', filters.endDate);
          }

          if (filters.limit) {
            query = query.limit(filters.limit);
          }

          const { data, error } = await query;
          if (error) throw error;
          return data || [];
        } catch (error) {
          console.error('Error getting reviews:', error);
          throw error;
        }
      },

      async createReview(companyId, reviewData) {
        try {
          const { data, error } = await supabase
            .from('reviews')
            .insert([{
              ...reviewData,
              company_id: companyId,
              created_at: new Date().toISOString()
            }])
            .select(`
              *,
              parent:parent_id (
                id,
                name,
                email,
                phone
              ),
              student:student_id (
                id,
                name,
                registration_number,
                class_name
              )
            `)
            .single();
          
          if (error) throw error;
          return data;
        } catch (error) {
          console.error('Error creating review:', error);
          throw error;
        }
      },

      async updateReview(reviewId, reviewData) {
        try {
          const { data, error } = await supabase
            .from('reviews')
            .update(reviewData)
            .eq('id', reviewId)
            .select(`
              *,
              parent:parent_id (
                id,
                name,
                email,
                phone
              ),
              student:student_id (
                id,
                name,
                registration_number,
                class_name
              )
            `)
            .single();
          
          if (error) throw error;
          return data;
        } catch (error) {
          console.error('Error updating review:', error);
          throw error;
        }
      },

      async deleteReview(reviewId) {
        try {
          const { error } = await supabase
            .from('reviews')
            .delete()
            .eq('id', reviewId);
          
          if (error) throw error;
          return true;
        } catch (error) {
          console.error('Error deleting review:', error);
          throw error;
        }
      },

      async getReviewStats(companyId, filters = {}) {
        try {
          let query = supabase
            .from('reviews')
            .select('rating')
            .eq('company_id', companyId);

          // Aplicar filtros opcionais
          if (filters.student_id) {
            query = query.eq('student_id', filters.student_id);
          }

          if (filters.startDate) {
            query = query.gte('created_at', filters.startDate);
          }

          if (filters.endDate) {
            query = query.lte('created_at', filters.endDate);
          }

          const { data, error } = await query;
          if (error) throw error;

          // Calcular estatísticas
          const total = data.length;
          if (total === 0) {
            return {
              total: 0,
              average: 0,
              distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
            };
          }

          const sum = data.reduce((acc, review) => acc + review.rating, 0);
          const average = sum / total;
          
          const distribution = data.reduce((acc, review) => {
            acc[review.rating] = (acc[review.rating] || 0) + 1;
            return acc;
          }, { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });

          return {
            total,
            average: Math.round(average * 100) / 100,
            distribution
          };
        } catch (error) {
          console.error('Error getting review stats:', error);
          throw error;
        }
      },

      async getStudentReviews(studentId, companyId) {
        try {
          const { data, error } = await supabase
            .from('reviews')
            .select(`
              *,
              parent:parent_id (
                id,
                name,
                email
              )
            `)
            .eq('student_id', studentId)
            .eq('company_id', companyId)
            .order('created_at', { ascending: false });

          if (error) throw error;
          return data || [];
        } catch (error) {
          console.error('Error getting student reviews:', error);
          throw error;
        }
      },      async canParentReview(parentId, studentId, companyId) {
        try {
          // Verificar se o responsável já avaliou este aluno
          const { data: existingReview, error } = await supabase
            .from('reviews')
            .select('id')
            .eq('parent_id', parentId)
            .eq('student_id', studentId)
            .eq('company_id', companyId)
            .single();

          if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
            throw error;
          }          return !existingReview; // Retorna true se NÃO existe avaliação
        } catch (error) {
          console.error('Error checking if parent can review:', error);
          return false;
        }
      }
  };
  
  dataHelpers = {
    async getDashboardMetrics() {
      return {
        todayTotal: 1247.30,
        monthTotal: 23891.50,
        studentsCount: 247,
        productsCount: 89,
        lowStockProducts: []
      };
    },
    async getProducts() { return []; },
    async createProduct() { return { id: 'mock-product' }; },    async getStudents() { return []; },
    async createStudent() { return { id: 'mock-student' }; },
    
    async getUsers() { return []; },
    async getUser(userId) { 
      return {
        id: userId,
        name: 'Mock User',
        email: 'mock@example.com',
        type: 'collaborator',
        permissions: null
      }; 
    },
    async createUser() { return { id: 'mock-user' }; },
    async updateUser() { return { id: 'mock-user' }; },
    async deleteUser() { return true; },
    
    async getBanks() { return []; },
    async createBank() { return { id: 'mock-bank' }; },
    async updateBank() { return { id: 'mock-bank' }; },
    async getBankById() { return null; },
    async deleteBank() { return true; },
    async testBankApiConnection() { 
      await new Promise(resolve => setTimeout(resolve, 2000));
      return { success: true, message: 'Mock: Conexão testada com sucesso!' }; 
    },
    
    async getSales() { return []; },
      async getSaleById(saleId) { 
      return {
        id: saleId,
        items: [
          { product_name: 'Produto Mock', quantity: 1, unit_price: 5.50, total_price: 5.50 }
        ],
        total_amount: 5.50,
        created_at: new Date().toISOString()
      }; 
    },
    
    async createSale() { return { id: 'mock-sale' }; },
      // Implementação mock do getTodayStats
    async getTodayStats(companyId) {
      console.log('🎭 MOCK getTodayStats para company:', companyId);
      return {
        todayTotal: 1247.30,
        salesCount: 15,
        averageTicket: 83.15,
        paymentMethodCounts: {
          'cash': 5,
          'credit': 7,
          'pix': 3
        },
        hourlyData: {
          9: { count: 3, total: 250.50 },
          10: { count: 4, total: 310.80 },
          12: { count: 5, total: 425.00 },
          15: { count: 3, total: 261.00 }
        },
        sales: [
          { time: '09:00', amount: 250.50, transactions: 3 },
          { time: '10:00', amount: 310.80, transactions: 4 },
          { time: '12:00', amount: 425.00, transactions: 5 },
          { time: '15:00', amount: 261.00, transactions: 3 }
        ],
        hourlyComparison: {
          current: 1247.30,
          yesterday: 980.50,
          change: 27 // Aumento de 27%
        }
      };
    },
      // Vendas recentes para o dashboard
    async getRecentSales(companyId, limit = 5) {
      console.log('🎭 MOCK getRecentSales para company:', companyId);
      return [
        {
          id: 'mock-sale-1',
          student: 'João Silva',
          class: '3A',
          items: 'Sanduíche Natural (1x), Suco de Laranja (1x)',
          time: '14:30',
          payment: 'Cartão',
          amount: 45.90
        },
        {
          id: 'mock-sale-2',
          student: 'Maria Santos',
          class: '2B',
          items: 'Chocolate (2x)',
          time: '14:00',
          payment: 'PIX',
          amount: 12.50
        },
        {
          id: 'mock-sale-3',
          student: 'Consumidor Final',
          class: '',
          items: 'Prato do Dia (1x)',
          time: '13:30',
          payment: 'Dinheiro',
          amount: 27.80
        }
      ].slice(0, limit);
    },    // Produtos com estoque baixo para o dashboard
    async getLowStockProducts(companyId, limit = 5) {
      console.log('🎭 MOCK getLowStockProducts para company:', companyId);
      return [
        { id: 'mock-product-1', name: 'Chocolate', current: 3, status: 'critical', price: 4.50 },
        { id: 'mock-product-2', name: 'Suco de Laranja', current: 5, status: 'low', price: 5.00 },
        { id: 'mock-product-3', name: 'Água Mineral', current: 8, status: 'low', price: 2.50 }
      ].slice(0, limit);
    },
    
    // Mock functions para pessoas
    async getPeople(companyId) {
      console.log('🎭 MOCK: getPeople chamado para company:', companyId);
      return [
        {
          id: 'mock-person-1',
          name: 'João Silva',
          email: 'joao@example.com',
          phone: '(11) 99999-9999',
          type: 'student',
          company_id: companyId,
          created_at: new Date().toISOString()
        },
        {
          id: 'mock-person-2', 
          name: 'Maria Santos',
          email: 'maria@example.com',
          phone: '(11) 88888-8888',
          type: 'collaborator',
          company_id: companyId,
          created_at: new Date().toISOString()
        },
        {
          id: 'mock-person-3',
          name: 'Pedro Costa',
          email: 'pedro@example.com',
          phone: '(11) 77777-7777',
          type: 'student',
          company_id: companyId,
          created_at: new Date().toISOString()
        }
      ];
    },
    async getPerson(personId) {
      return {
        id: personId,
        name: 'Mock Person',
        email: 'mock@example.com',
        phone: '(11) 99999-9999',
        type: 'student',
        created_at: new Date().toISOString()
      };
    },
    async createPerson(companyId, personData) { 
      return { 
        id: `mock-person-${Date.now()}`,
        ...personData,
        company_id: companyId,
        created_at: new Date().toISOString()
      }; 
    },
    async updatePerson(personId, personData) { 
      return { 
        id: personId,
        ...personData,
        updated_at: new Date().toISOString()
      }; 
    },
    async deletePerson(personId) { 
      console.log('Mock: Deletando pessoa', personId);
      return true; 
    },

    async authenticateParent(email, password) {
      console.log('🎭 MOCK: Autenticação de responsável:', email);
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      if (email === 'paulo@madalozzosistemas.com.br') {
        return {
          parent: {
            id: 'mock-parent-1',
            name: 'Paulo Responsável',
            email: 'paulo@madalozzosistemas.com.br',
            phone: '(11) 99999-9999',
            cpf: '123.456.789-00',
            address: null
          },
          primaryStudent: {
            id: 'mock-student-1',
            name: 'João Paulo',
            email: 'joao@student.com',
            phone: '(11) 88888-8888',
            registration_number: 'EST001',
            class_name: '3º Ano A',
            birth_date: '2010-05-15',
            balance: 125.50,
            photo_url: null,
          }
        };
      }
      
      throw new Error('Email ou senha incorretos');
    },

    async getStudentBalance(studentId) {
      try {
        console.log('💰 Buscando saldo real do estudante:', studentId);
        
        const { data: student, error } = await supabase
          .from('students')
          .select('balance')
          .eq('id', studentId)
          .single();
        
        if (error) {
          console.error('Erro ao buscar saldo do estudante:', error);
          throw error;
        }
        
        if (!student) {
          console.warn('Estudante não encontrado:', studentId);
          return 0;
        }
        
        console.log(`💰 Saldo atual do estudante ${studentId}:`, student.balance);
        return parseFloat(student.balance) || 0;
      } catch (error) {
        console.error('Erro ao buscar saldo:', error);
        throw error;
      }
    },

    async getStudentsByParentEmail(parentEmail) {
      console.log('🎭 MOCK: Buscando alunos do responsável:', parentEmail);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (parentEmail === 'paulo@madalozzosistemas.com.br') {
        return [
          {
            id: 'mock-student-1',
            name: 'João Paulo',
            email: 'joao@student.com',
            phone: '(11) 88888-8888',
            registration_number: 'EST001',
            class_name: '3º Ano A',
            birth_date: '2010-05-15',
            balance: 125.50,
            photo_url: null,
            parent_name: 'Paulo Responsável',
            credit_limit: 200.00,
            payment_type: 'prepaid',
            active: true,
            company_id: 'mock-company-1',
            created_at: new Date().toISOString()
          },
          {
            id: 'mock-student-2',
            name: 'Maria Paula',
            email: 'maria@student.com',
            phone: '(11) 77777-7777',
            registration_number: 'EST002',
            class_name: '1º Ano B',
            birth_date: '2012-08-20',
            balance: 89.30,
            photo_url: null,
            parent_name: 'Paulo Responsável',
            credit_limit: 150.00,
            payment_type: 'prepaid',
            active: true,
            company_id: 'mock-company-1',
            created_at: new Date().toISOString()
          }
        ];
      }
      
      return [];
    },

    async getStudentTransactions(studentId, options = {}) {
      console.log('🎭 MOCK: Buscando transações do estudante:', studentId);
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const { limit = 50 } = options;
      
      const mockTransactions = [
        {
          id: 'trans-1',
          date: new Date().toISOString(),
          type: 'purchase',
          description: 'Lanche da manhã - Pão de açúcar + suco',
          amount: -8.50,
          balance_after: 125.50,
          payment_method: 'balance',
          status: 'completed'
        },
        {
          id: 'trans-2',
          date: new Date(Date.now() - 86400000).toISOString(),
          type: 'credit',
          description: 'Recarga via PIX',
          amount: 50.00,
          balance_after: 134.00,
          payment_method: 'pix',
          status: 'completed'
        },
        {
          id: 'trans-3',
          date: new Date(Date.now() - 172800000).toISOString(),
          type: 'purchase',
          description: 'Almoço - Prato executivo',
          amount: -12.00,
          balance_after: 84.00,
          payment_method: 'balance',
          status: 'completed'
        },
        {
          id: 'trans-4',
          date: new Date(Date.now() - 259200000).toISOString(),
          type: 'purchase',
          description: 'Lanche da tarde - Sanduíche natural',
          amount: -6.50,
          balance_after: 96.00,
          payment_method: 'balance',
          status: 'completed'
        }
      ];
      
      return mockTransactions.slice(0, limit);
    },

    async getStudentFinancialSummary(studentId, monthsBack = 3) {
      console.log('🎭 MOCK: Buscando resumo financeiro do estudante:', studentId);
      await new Promise(resolve => setTimeout(resolve, 600));
      
      return {
        totalCredits: 150.00,
        totalDebits: 87.50,
        totalTransactions: 15,
        monthlyData: {
          'Jun 2024': { credits: 50.00, debits: 25.00, count: 8 },
          'Mai 2024': { credits: 75.00, debits: 35.50, count: 12 },
          'Abr 2024': { credits: 25.00, debits: 27.00, count: 7 }
        }
      };
    },

    async getWeeklyMenu(companyId) {
      console.log('🎭 MOCK: Buscando cardápio da semana para company:', companyId);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      return [
        {
          id: 'menu-1',
          date: new Date().toISOString().split('T')[0],
          meal_type: 'lunch',
          name: 'Cardápio de Segunda-feira',
          description: 'Refeição completa e nutritiva',
          items: ['Arroz branco', 'Feijão preto', 'Frango grelhado', 'Salada verde', 'Suco natural de laranja'],
          active: true
        },
        {
          id: 'menu-2',
          date: new Date(Date.now() + 86400000).toISOString().split('T')[0],
          meal_type: 'lunch',
          name: 'Cardápio de Terça-feira',
          description: 'Opções variadas e saborosas',
          items: ['Arroz integral', 'Feijão carioca', 'Peixe assado', 'Legumes refogados', 'Suco de uva'],
          active: true
        }
      ];
    },

    async getParentNotifications(parentEmail) {
      console.log('🎭 MOCK: Buscando notificações para:', parentEmail);
      await new Promise(resolve => setTimeout(resolve, 400));
      
      return [
        {
          id: 'notif-1',
          title: 'Bem-vindo ao Portal dos Pais!',
          message: 'Agora você pode acompanhar todas as atividades do seu filho na cantina.',
          type: 'info',
          read: false,
          created_at: new Date().toISOString()
        },
        {
          id: 'notif-2',
          title: 'Saldo baixo',
          message: 'O saldo de João Paulo está abaixo de R$ 20,00. Considere fazer uma recarga.',
          type: 'warning',
          read: false,
          created_at: new Date(Date.now() - 3600000).toISOString()
        },
        {
          id: 'notif-3',
          title: 'Cardápio da semana atualizado',
          message: 'Confira o novo cardápio disponível na aba Cardápio.',
          type: 'info',
          read: true,
          created_at: new Date(Date.now() - 86400000).toISOString()
        }
      ];
    },

    async addStudentCredit(studentId, amount, description = 'Recarga manual', paymentMethod = 'manual') {
      console.log('🎭 MOCK: Adicionando crédito ao estudante:', studentId, 'Valor:', amount);
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Simular sucesso da recarga
      const previousBalance = 125.50; // Mock do saldo anterior
      const newBalance = previousBalance + parseFloat(amount);
      const mockTransactionId = 'mock-transaction-' + Date.now();
      
      console.log('🎭 MOCK: Simulando criação de transação financeira:', mockTransactionId);
      
      return {
        success: true,
        previousBalance: previousBalance,
        newBalance: newBalance,
        creditAmount: parseFloat(amount),
        student_id: studentId,
        transaction_id: mockTransactionId,
        transaction: {
          id: mockTransactionId,
          type: 'credit',
          description: description,
          amount: parseFloat(amount),
          payment_method: paymentMethod,
          status: 'completed'
        }
      };
    },

    // Funções de estoque
    async getInventoryMovements(companyId) {
      return [
        {
          id: 'mock-movement-1',
          company_id: companyId,
          product_id: 'mock-product-1',
          type: 'in',
          quantity: 10,
          cost_per_unit: 5.50,
          reason: 'Compra',
          notes: 'Movimento de teste',
          created_at: new Date().toISOString(),
          products: {
            id: 'mock-product-1',
            name: 'Produto de Teste',
            category: 'Teste'
          },
          creator: {
            id: 'mock-user-1',
            name: 'Usuário de Teste'
          }
        }
      ];
    },
    async createInventoryMovement(companyId, movementData) {
      return {
        id: `mock-movement-${Date.now()}`,
        ...movementData,
        company_id: companyId,
        created_at: new Date().toISOString(),
        products: {
          id: movementData.product_id,
          name: 'Produto Mock',
          category: 'Mock'
        },
        creator: {
          id: movementData.created_by,
          name: 'Usuário Mock'
        }
      };
    },

    // Funções de mensalidades
    async getMonthlyPlans(companyId) {
      return [
        {
          id: 'mock-plan-1',
          name: 'Plano Básico',
          monthly_value: 50.00,
          description: 'Plano mock para testes',
          company_id: companyId
        }
      ];
    },
    async getMonthlySubscribers(companyId) {
      return [
        {
          id: 'mock-subscriber-1',
          student_id: 'mock-student-1',
          monthly_plan_id: 'mock-plan-1',
          status: 'active',
          company_id: companyId,
          students: {
            id: 'mock-student-1',
            name: 'Aluno de Teste',
            registration_number: '001',
            class_name: '1º Ano'
          },
          monthly_plans: {
            id: 'mock-plan-1',
            name: 'Plano Básico',
            monthly_value: 50.00
          }
        }
      ];
    },
    async getMonthlyInvoices(companyId) {
      return [];
    },
    async createMonthlySubscriber(companyId, subscriberData) {
      return {
        id: `mock-subscriber-${Date.now()}`,
        ...subscriberData,
        company_id: companyId,
        created_at: new Date().toISOString()
      };
    },

    // Mock functions para configurações
    async getCompanySettings(companyId) {
      return {
        general: {
          companyName: 'Cantina Demo',
          cnpj: '12.345.678/0001-90',
          email: 'contato@cantina.com',
          phone: '(11) 99999-9999',
          address: 'Rua das Flores, 123',
          city: 'São Paulo',
          state: 'SP',
          zipCode: '01234-567',
          timezone: 'America/Sao_Paulo',
          currency: 'BRL',
          taxRate: 0,
          language: 'pt-BR'
        },
        whatsapp: {
          enabled: false,
          apiUrl: '',
          apiKey: '',
          instanceName: '',
          webhookUrl: '',
          autoResponse: true,
          welcomeMessage: 'Olá! Bem-vindo à nossa cantina. Como posso ajudá-lo?',
          balanceMessage: 'Seu saldo atual é: R$ {balance}',
          lowBalanceAlert: true,
          lowBalanceThreshold: 10.00,
          paymentConfirmation: true,
          receiptMessage: 'Recibo da sua compra:\n{receipt}',
          businessHours: {
            enabled: true,
            start: '07:00',
            end: '17:00',
            message: 'Nosso atendimento funciona das 7h às 17h. Retornaremos em breve!'
          }
        },        notifications: {
          emailNotifications: true,
          whatsappNotifications: false,
          lowStockAlert: true,
          lowBalanceAlert: true
        }
      };
    },

    async updateCompanySettings(companyId, settings) {
      console.log('Mock: Salvando configurações:', settings);
      return { success: true, id: 'mock-settings' };
    },

    async testWhatsAppConnection(apiUrl, apiKey, instanceName) {
      // Simula teste de conexão
      await new Promise(resolve => setTimeout(resolve, 2000));
      return Math.random() > 0.5 ?        { success: true, connected: true, message: 'Conectado com sucesso!' } :
        { success: false, connected: false, message: 'Erro: Não foi possível conectar à instância' };
    },
    async sendWhatsAppMessage(apiUrl, apiKey, instanceName, phoneNumber, message) {
      // Simula envio de mensagem
      await new Promise(resolve => setTimeout(resolve, 1500));
      return Math.random() > 0.3 ?
        { success: true, message: 'Mensagem enviada com sucesso!' } :
        { success: false, message: 'Erro: Não foi possível enviar a mensagem' };    }
    };

    console.log('Supabase configured successfully');
}

// Mock data when Supabase is not configured
if (!dataHelpers) {
  dataHelpers = {
    // Mock functions para reviews
    async getReviews(companyId, filters = {}) {
        const mockReviews = [
          {
            id: '1',
            parent_id: '1',
            parent_name: 'Maria Silva',
            student_id: '1', 
            company_id: companyId,
            rating: 5,
            comment: 'Sistema excelente! Muito fácil de usar e o suporte é ótimo.',
            created_at: '2024-06-01T10:00:00Z',
            parent: {
              id: '1',
              name: 'Maria Silva',
              email: 'maria@email.com',
              phone: '(11) 99999-9999'
            },
            student: {
              id: '1',
              name: 'João Silva',
              registration_number: '2024001',
              class_name: '5º Ano A'
            }
          },
          {
            id: '2',
            parent_id: '2',
            parent_name: 'João Souza',
            student_id: '2',
            company_id: companyId,
            rating: 4,
            comment: 'Gostei bastante, mas poderia ter mais opções de relatório.',
            created_at: '2024-06-02T14:30:00Z',
            parent: {
              id: '2',
              name: 'João Souza',
              email: 'joao@email.com',
              phone: '(11) 88888-8888'
            },
            student: {
              id: '2',
              name: 'Ana Souza',
              registration_number: '2024002',
              class_name: '3º Ano B'
            }
          },
          {
            id: '3',
            parent_id: '3',
            parent_name: 'Ana Costa',
            student_id: '3',
            company_id: companyId,
            rating: 3,
            comment: 'Sistema funciona bem, mas às vezes fica lento.',
            created_at: '2024-06-03T16:45:00Z',
            parent: {
              id: '3',
              name: 'Ana Costa',
              email: 'ana@email.com',
              phone: '(11) 77777-7777'
            },
            student: {
              id: '3',
              name: 'Pedro Costa',
              registration_number: '2024003',
              class_name: '2º Ano C'
            }
          }
        ];

        let filteredReviews = mockReviews;

        if (filters.rating) {
          filteredReviews = filteredReviews.filter(r => r.rating === filters.rating);
        }

        if (filters.student_id) {
          filteredReviews = filteredReviews.filter(r => r.student_id === filters.student_id);
        }

        if (filters.parent_id) {
          filteredReviews = filteredReviews.filter(r => r.parent_id === filters.parent_id);
        }

        return filteredReviews;
      },

      async createReview(companyId, reviewData) {
        return {
          id: `mock-review-${Date.now()}`,
          ...reviewData,
          company_id: companyId,
          created_at: new Date().toISOString(),
          parent: {
            id: reviewData.parent_id,
            name: reviewData.parent_name,
            email: 'parent@email.com'
          },
          student: {
            id: reviewData.student_id,
            name: 'Aluno Teste',
            registration_number: '2024001'
          }
        };
      },

      async updateReview(reviewId, reviewData) {
        return {
          id: reviewId,
          ...reviewData,
          updated_at: new Date().toISOString()
        };
      },

      async deleteReview(reviewId) {
        return true;
      },

      async getReviewStats(companyId, filters = {}) {
        return {
          total: 15,
          average: 4.1,
          distribution: { 1: 1, 2: 2, 3: 3, 4: 5, 5: 4 }
        };
      },

      async getStudentReviews(studentId, companyId) {
        const allReviews = await this.getReviews(companyId);
        return allReviews.filter(review => review.student_id === studentId);
      },

      async canParentReview(parentId, studentId, companyId) {
        return true; // Mock sempre permite avaliação
      }
    };
  }

// Função para inserir avaliação (mantida para compatibilidade)
export async function submitReview({ parent_id, parent_name, student_id, company_id, rating, comment }) {
  if (dataHelpers && dataHelpers.createReview) {
    return await dataHelpers.createReview(company_id, {
      parent_id,
      parent_name,
      student_id,
      rating,
      comment
    });
  }
  
  const { data, error } = await supabase
    .from('reviews')
    .insert([{ parent_id, parent_name, student_id, company_id, rating, comment }]);
  return { data, error };
}

// Função para buscar avaliações (mantida para compatibilidade)
export async function fetchReviews(company_id, rating = null) {
  if (dataHelpers && dataHelpers.getReviews) {
    return await dataHelpers.getReviews(company_id, { rating });
  }
  
  let query = supabase
    .from('reviews')
    .select('*')
    .eq('company_id', company_id)
    .order('created_at', { ascending: false });

  if (rating) {
    query = query.eq('rating', rating);
  }

  const { data, error } = await query;
  return { data, error };
}

export { supabase, authHelpers, dataHelpers };
export default supabase;